import { Link, Navigate, Outlet, useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './layout-header.css';
import '../chat/chatTheme.css';
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarTrigger,
} from '../ui/sidebar';
import { useAuthStore } from '../../store/authStore';
import { useDevStore } from '../../store/useDevStore';
import { GlobalChatWidget } from '../chat/GlobalChatWidget';
import { api } from '../../api/client';

import { useChamadaStore } from '../../store/chamadaStore';
import { useUiStore } from '../../store/uiStore';
import { toast } from 'sonner';
import { UserAvatar } from '../ui/UserAvatar';
import { canAccessEquipePath, getFirstAllowedEquipePath, hasPermission } from '../../auth/permissions';
import { LogOut, ChevronDown, ChevronUp, Copy, Check, Menu, X, User, Settings, Shield, CheckCircle2, Lock, Bell, BellRing, Loader2, ChevronsLeft, ChevronsRight, AlertTriangle, Home, MessageSquare, Calendar, School, Megaphone, Video, Sun, QrCode, Database, Palette, BookOpen, GraduationCap, Mail, Info } from 'lucide-react';
import { useState, useEffect, useMemo, useRef, useLayoutEffect, type CSSProperties } from 'react';
import { useScreenWakeLock } from '../../hooks/useScreenWakeLock';
import { audioAlerts } from '../../utils/audioAlerts';
import type { LucideIcon } from 'lucide-react';
import type { SidebarBadge } from '../../hooks/useSetupBadges';
import { PwaPushEnforcer } from '../security/PwaPushEnforcer';
import { PasswordConfirmModal } from '../ui/PasswordConfirmModal';
import { HelpButton } from '../help/HelpButton';
import { HelpDialog } from '../help/HelpDialog';
import { ACWalkthrough } from '../help/Walkthrough';
import { useHelp } from '../../hooks/useHelp';
import { useAutoTourOnce } from '../../features/training/useAutoTourOnce';
import { TourPrepBanner } from '../../features/training/TourPrepBanner';
import {
  areToursEnabled,
  markTourDone,
  navTourId,
  resetTourDone,
  sectionTourId,
  setToursEnabled,
} from '../../features/training/tourPrefs';
import {
  getBuiltInTour,
  getBuiltInTourForHelp,
  resolveTourProfile,
} from '../../features/training/tourRegistry';
import { capturePageMap, pageTourId } from '../../features/training/capturePageMap';
import { ensureTour, regenerateTour } from '../../features/training/tourApi';
import { useWalkthroughStore } from '../../store/walkthroughStore';
import { Modal } from '../ui/Modal';
import { QrCodeVinculo } from '../wizard/QrCodeVinculo';
import {
  ADMIN_SETUP_PATH_TO_ETAPA,
  type EtapaSetup,
} from '../../hooks/useSetupStatus';
import { listPendingApprovals } from '../../security/deviceApprovalApi';
import { registerAuthenticatedPushToken } from '../../security/pushApi';
import { resolveSidebarTextColor } from '../../theme/profileBrandColors';
import { AuditFooterBanner } from '../../features/telemetry/AuditFooterBanner';
import { ComunicadoVisualizacaoModal } from '../comunicados/ComunicadoVisualizacaoModal';
import {
  getUserDevices,
  revokeUserDevice,
  getInactiveDevices,
  formatRelativeDeviceAccess,
  type Dispositivo,
} from '../../utils/userDevices';

const API = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Rota canônica de perfil para cada tipo de usuário no sistema.
 * Garante que cada perfil abra seu respectivo ambiente sem desvios.
 */
export function getPerfilRouteForUser(perfil?: string | null, currentPath = ''): string {
  const p = (perfil || '').toUpperCase();
  if (p === 'ADMIN' || p === 'MASTER' || p === 'SISTEMA') return '/master/perfil';
  if (p === 'MUNICIPAL') return '/municipal/perfil';
  if (p === 'ESTADUAL') return '/estadual/perfil';
  if (p === 'REDE') return '/rede/perfil';
  if (p === 'PROFESSOR') return '/professor/perfil';
  if (p === 'RESPONSAVEL') return '/responsavel/perfil';
  if (p === 'ALUNO') return '/aluno/perfil';
  if (p === 'ESCOLA') {
    if (currentPath.startsWith('/equipe')) return '/equipe/perfil';
    return '/admin/perfil';
  }
  if (currentPath.startsWith('/equipe')) return '/equipe/perfil';
  return '/admin/perfil';
}

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badgeKey?: string;
  subItems?: { to: string; label: string; badgeKey?: string }[];
}

export interface NavSection {
  title: string | null;
  items: NavItem[];
  adminOnly?: boolean;
  /** Secao sempre visivel, sem seta de colapsar (ex.: Escola, Operacional). */
  alwaysExpanded?: boolean;
}

/** Chaves normalizadas com normalizeNavLabel (minusculas, sem acento, / → espaco) */
const SIDEBAR_ITEM_SUBTITLES: Record<string, string> = {
  dashboard: 'Visao resumida diaria',
  turnos: 'Periodos letivos',
  materias: 'Disciplinas e areas',
  professores: 'Corpo docente',
  turmas: 'Classes e vagas',
  alunos: 'Matriculas e fichas',
  responsaveis: 'Familias e contatos',
  equipe: 'Permissoes e cargos',
  dispositivos: 'TVs e leitores',
  chamada: 'Frequencia do dia',
  'governanca chamadas': 'Auditoria de chamadas',
  comunicados: 'Avisos e circulares',
  midias: 'Fotos e folders',
  'acesso portaria': 'Portaria e entradas',
  redes: 'Cadastro de redes',
  estados: 'Unidades federativas',
  municipios: 'Prefeituras',
  escolas: 'Unidades escolares',
  usuarios: 'Contas de acesso',
  chamadas: 'Gestao consolidada',
  acesso: 'Portaria e eventos',
  'tv grade': 'Telas e grades',
  frequencia: 'Registro de presencas',
  portaria: 'Controle de acesso',
  incidentes: 'Alertas operacionais',
  relatorios: 'Indicadores escola',
  'banco de midias': 'Fotos e arquivos',
  configuracoes: 'Preferencias do sistema',
  'diario de classe': 'Aulas e conteudos',
  'minhas turmas': 'Turmas atribuidas',
  'meus alunos': 'Alunos das suas turmas',
  avaliacoes: 'Notas e criterios',
  observacoes: 'Registro pedagogico',
  'meu perfil': 'Conta e preferencias',
  inicio: 'Resumo rapido',
  'meus filhos': 'Dependentes vinculados',
  notificacoes: 'Alertas da escola',
  justificativas: 'Faltas e abonos',
  'minha grade': 'Horarios de aula',
  'mapa estrategico': 'Escolas e familias',
  'mapa de cobertura': 'Escolas por localizacao',
  // Comercial
  funil: 'Pipeline de vendas',
  oportunidades: 'Prospects e leads',
  contratos: 'Acordos vigentes',
  // RFID
  'dashboard rfid': 'Visao consolidada RFID',
  'dispositivos rfid': 'Leitores e antenas',
  credenciais: 'Cartoes e pulseiras',
  // Suporte
  chamados: 'Tickets abertos',
  // Operacoes
  implantacoes: 'Setup de escolas',
  visitas: 'Visitas tecnicas',
  treinamentos: 'Capacitacoes',
  // Produtividade
  agenda: 'Eventos e compromissos',
  tarefas: 'Kanban e checklist',
};



function normalizeNavLabel(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\//g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getSidebarSubtitle(label: string): string | undefined {
  return SIDEBAR_ITEM_SUBTITLES[normalizeNavLabel(label)];
}

/** Props do layout base multi-perfil */
export interface BaseLayoutProps {
  sections: NavSection[];
  headerTitle: string;
  brandLabel?: string;
  /** Subtitulo abaixo do titulo na sidebar (ex: 'Comando Master') */
  brandSubtitle?: string;
  /** Accent do perfil (ícones / destaques) */
  brandColor?: string;
  /** Fundo da sidebar (gradiente do ambiente desktop). Se omitido, usa brandColor sólido. */
  brandGradient?: string;
  /** Badges de setup para itens do sidebar */
  badges?: Record<string, SidebarBadge>;
  /** Tamanho dos itens de navegação: 'sm' (padrão compacto) | 'md' (maior, para menus enxutos) */
  navSize?: 'sm' | 'md';
  /** Largura do sidebar expandido em lg (px). Default 280 */
  sidebarWidth?: number;
  setupGuard?: {
    etapas: EtapaSetup[];
    progresso?: number;
    etapasConcluidas?: number;
    totalEtapas?: number;
  };
  useShadcnSidebar?: boolean;
}

function cleanBrowserName(raw: string | undefined | null): string {
  if (!raw) return 'Dispositivo';
  const lower = raw.toLowerCase();
  if (lower.includes('chrome') || lower.includes('google chrome')) return 'Google Chrome';
  if (lower.includes('firefox')) return 'Mozilla Firefox';
  if (lower.includes('safari') && !lower.includes('chrome')) return 'Safari';
  if (lower.includes('edge') || lower.includes('edg/')) return 'Microsoft Edge';
  if (lower.includes('opera') || lower.includes('opr/')) return 'Opera';
  if (lower.includes('chromium')) return 'Chromium';
  if (raw.includes('";v="') || raw.includes(';v=')) {
    const parts = raw.split(',').map(p => p.trim().replace(/^"/, '').replace(/"$/, ''));
    const cleanParts = parts.filter(p => !p.toLowerCase().includes('not') && !p.toLowerCase().includes('brand'));
    if (cleanParts.length > 0) {
      const first = cleanParts[0].split(';')[0];
      return first.replace(/"/g, '');
    }
  }
  return raw;
}

/**
 * Layout base reutilizável por todos os perfis.
 * Recebe as seções de menu e o título do header.
 */
//#region AC_BLOCK base-layout.base-layout -- Shell autenticado multi-perfil
// @ac-purpose Renderiza o layout autenticado compartilhado, sidebar, aprovacoes pendentes, push e widget global de chat.
// @ac-related react-app/src/App.tsx#AuthenticatedRoutes
// @ac-related react-app/src/components/chat/GlobalChatWidget.tsx#GlobalChatWidget
// @ac-related docs_secutiry/matriz-rotas-repetidas-reducao.md#Push-e-FCM
// @ac-routes /security/approve/:id, authenticated-layout
// @ac-tests node tools/codex-kit.mjs check react-tsc
function ShadcnSidebarContent({
  sections,
  logout,
  nomeEscola,
  headerTitle,
  brandSubtitle,
  brandColor,
  user,
}: {
  sections: NavSection[];
  logout: () => void;
  nomeEscola?: string | null;
  headerTitle?: string;
  brandSubtitle?: string;
  brandColor?: string;
  user: any;
}) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const shadcnScrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const saved = sessionStorage.getItem('ac_sidebar_scroll_pos');
    if (saved && shadcnScrollRef.current) {
      shadcnScrollRef.current.scrollTop = Number(saved);
    }
  }, [location.pathname]);

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
      {/* Brand Header */}
      <div className="flex h-14 items-center px-4 border-b border-sidebar-border gap-2.5">
        <img src="/icons/logo-shield.svg" alt="Aluno Conectado" style={{ width: 32, height: 32 }} />
        <div className="flex flex-col justify-center min-w-0">
          <span className="text-[9px] uppercase font-bold tracking-wider opacity-75 truncate">
            {formatKicker(headerTitle) || 'Sistema'}
          </span>
          <span className="font-bold text-xs truncate text-sidebar-foreground">
            {brandSubtitle || 'Comando Master'}
          </span>
        </div>
      </div>

      {/* Main navigation sections */}
      <div
        ref={shadcnScrollRef}
        onScroll={(e) => {
          sessionStorage.setItem('ac_sidebar_scroll_pos', e.currentTarget.scrollTop.toString());
        }}
        className="flex-1 overflow-y-auto py-2"
      >
        {sections.map((section, idx) => (
          <SidebarGroup key={idx}>
            {section.title && (
              <SidebarGroupLabel className="text-[10px] font-bold uppercase tracking-wider text-sidebar-foreground/40 px-4 py-1.5 block">
                {section.title}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item, itemIdx) => {
                  const hasSubs = item.subItems && item.subItems.length > 0;
                  const isGroupExpanded = !!expandedGroups[item.label];
                  const isActive = location.pathname.startsWith(item.to) && item.to !== '#';

                  return (
                    <SidebarMenuItem key={itemIdx} className="px-2 py-0.5">
                      {hasSubs ? (
                        <>
                          <SidebarMenuButton
                            onClick={() => toggleGroup(item.label)}
                            className="w-full text-left flex items-center justify-between text-sidebar-foreground hover:bg-sidebar-accent px-3 py-2 rounded-lg text-xs"
                          >
                            <span className="flex items-center gap-2">
                              <item.icon size={15} />
                              <span className="font-medium">{item.label}</span>
                            </span>
                            <ChevronDown size={12} className={`transform transition-transform ${isGroupExpanded ? 'rotate-180' : ''}`} />
                          </SidebarMenuButton>

                          {isGroupExpanded && (
                            <SidebarMenuSub className="ml-4 pl-2 border-l border-sidebar-border flex flex-col gap-0.5 mt-1">
                              {item.subItems?.map((sub, subIdx) => {
                                const isSubActive = location.pathname === sub.to;
                                return (
                                  <SidebarMenuSubItem key={subIdx}>
                                    <SidebarMenuSubButton
                                      isActive={isSubActive}
                                      render={
                                        <Link
                                          to={sub.to}
                                          data-tour={navTourId(sub.to)}
                                          className={`text-xs block py-1 px-2 rounded-md ${
                                            isSubActive
                                              ? 'bg-sidebar-accent text-sidebar-foreground font-semibold'
                                              : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/50'
                                          }`}
                                        />
                                      }
                                    >
                                      {sub.label}
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          )}
                        </>
                      ) : (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip={item.label}
                          render={
                            <Link
                              to={item.to}
                              data-tour={item.to !== '#' ? navTourId(item.to) : undefined}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                                isActive
                                  ? 'bg-sidebar-accent text-sidebar-foreground font-semibold'
                                  : 'text-sidebar-foreground hover:bg-sidebar-accent'
                              }`}
                            />
                          }
                        >
                          <item.icon size={15} />
                          <span className="font-medium">{item.label}</span>
                        </SidebarMenuButton>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </div>

      {/* Footer / User Profile */}
      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem className="px-2">
            <SidebarMenuButton
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 text-red-500 hover:bg-red-500/10 rounded-lg text-xs font-semibold"
            >
              <LogOut size={15} />
              <span>Sair</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </div>
  );
}

// Helper para extrair o contexto puro sem prefixos redundantes (ex: 'Painel Municipal' -> 'Municipal')
const formatKicker = (title?: string) => {
  if (!title) return '';
  const cleaned = title
    .replace(/^painel\s+(da|do|de|dos|das)?\s*/i, '')
    .replace(/^portal\s+(da|do|de|dos|das)?\s*/i, '')
    .replace(/^sistema\s+(da|do|de|dos|das)?\s*/i, '')
    .trim();
  return cleaned || title;
};

export function BaseLayout({ sections: rawSections, headerTitle, brandLabel = 'AC', brandSubtitle, brandColor = '#1B365D', brandGradient, badges = {}, navSize = 'sm', sidebarWidth = 260, setupGuard, useShadcnSidebar = false }: BaseLayoutProps) {
  const { settings } = useDevStore();
  const showVideoAulas = settings.showVideoAulas ?? true;
  const showSimulador = settings.showSimulador ?? true;
  const showHelpIcon = settings.showHelpIcon ?? true;

  const sections = useMemo(() => {
    return rawSections.map(sec => ({
      ...sec,
      items: sec.items.filter(item => {
        if (item.label === 'Video Aulas' && !showVideoAulas) return false;
        if (item.label === 'Simulador' && !showSimulador) return false;
        return true;
      })
    }));
  }, [rawSections, showVideoAulas, showSimulador]);

  const { isAuthenticated, user, logout, perfil, cargo, nomeEscola, isImpersonating } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  const help = useHelp();
  const startWalkthrough = useWalkthroughStore((s) => s.startWalkthrough);
  useAutoTourOnce();

  const tourProfile = resolveTourProfile(perfil, cargo);
  const builtInHelp = getBuiltInTour(tourProfile, location.pathname);
  const [toursOn, setToursOn] = useState(() => areToursEnabled());
  const [regeneratingTour, setRegeneratingTour] = useState(false);

  useEffect(() => {
    const sync = () => setToursOn(areToursEnabled());
    window.addEventListener('ac-tours-pref-changed', sync);
    return () => window.removeEventListener('ac-tours-pref-changed', sync);
  }, []);

  const handleStartTour = async () => {
    help.closeHelp();
    const path = location.pathname;
    const tourId = pageTourId(path);
    resetTourDone(tourProfile, tourId);
    if (builtInHelp) resetTourDone(tourProfile, builtInHelp.tourId);

    try {
      const capture = await capturePageMap({ withScreenshot: false, ambiente: 'APP' });
      const result = await ensureTour({
        perfil: tourProfile,
        urlPath: path,
        cargo,
        pageTitle: help.pageTitle || capture.pageTitle,
        regions: capture.regions,
        elements: capture.elements,
        contentHash: capture.contentHash,
        ambiente: 'APP',
      });
      const steps =
        result.passos?.length
          ? result.passos
          : help.walkthroughSteps.length > 0
            ? help.walkthroughSteps
            : builtInHelp?.steps || [];
      if (!steps.length) {
        toast.error('Não foi possível montar o tour desta página.');
        return;
      }
      startWalkthrough(steps, { pageResenha: result.pageResenha });
      markTourDone(tourProfile, tourId);
    } catch {
      const steps =
        help.walkthroughSteps.length > 0
          ? help.walkthroughSteps
          : builtInHelp?.steps || [];
      if (!steps.length) {
        toast.error('Tour indisponível no momento.');
        return;
      }
      startWalkthrough(steps);
    }
  };

  const handleRegenerateTour = async () => {
    setRegeneratingTour(true);
    try {
      const path = location.pathname;
      const tourId = pageTourId(path);
      resetTourDone(tourProfile, tourId);
      const capture = await capturePageMap({ withScreenshot: true, ambiente: 'APP' });
      const result = await regenerateTour({
        perfil: tourProfile,
        urlPath: path,
        cargo,
        pageTitle: help.pageTitle || capture.pageTitle,
        regions: capture.regions,
        elements: capture.elements,
        contentHash: capture.contentHash,
        screenshotBase64: capture.screenshotBase64,
        pageHtml: capture.pageHtml,
        ambiente: 'APP',
      });
      if (!result.passos?.length) {
        toast.error('A IA não conseguiu gerar o tour. Tente de novo.');
        return;
      }
      help.closeHelp();
      startWalkthrough(result.passos, { pageResenha: result.pageResenha });
      markTourDone(tourProfile, tourId);
      toast.success(
        result.fonte === 'NAV'
          ? 'Tour regenerado (modo menu).'
          : 'Tour regenerado com IA.',
      );
    } catch {
      toast.error('Falha ao regenerar o tour.');
    } finally {
      setRegeneratingTour(false);
    }
  };

  const handleToggleTours = (enabled: boolean) => {
    setToursEnabled(enabled);
    setToursOn(enabled);
    if (enabled) {
      resetTourDone(tourProfile, pageTourId(location.pathname));
      if (builtInHelp) resetTourDone(tourProfile, builtInHelp.tourId);
    }
  };

  const outlet = useOutlet();
  const [isDesativadosUnlocked, setIsDesativadosUnlocked] = useState(false);
  const [unlockFolderTarget, setUnlockFolderTarget] = useState<string | null>(null);
  const [currentOutlet, setCurrentOutlet] = useState<React.ReactNode>(outlet);
  const [transitionQueue, setTransitionQueue] = useState<{
    id: string;
    pathname: string;
    outlet: React.ReactNode;
    state: 'entering' | 'exiting';
    direction: 'forward' | 'backward';
    durationMs: number;
  }[]>([]);
  const transitionStartRef = useRef<number>(0);
  const transitionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastPathname = useRef(location.pathname);
  const outletRef = useRef(outlet);
  const currentOutletRef = useRef(currentOutlet);
  const transitionQueueRef = useRef(transitionQueue);
  outletRef.current = outlet;
  currentOutletRef.current = currentOutlet;
  transitionQueueRef.current = transitionQueue;

  // Mapeia ordem visual dos links do menu
  const menuItems = useMemo(() => {
    const list: string[] = [];
    sections.forEach(s => {
      s.items.forEach(item => {
        if (item.to && item.to !== '#') {
          list.push(item.to.toLowerCase());
        }
        if (item.subItems) {
          item.subItems.forEach(sub => {
            if (sub.to) {
              list.push(sub.to.toLowerCase());
            }
          });
        }
      });
    });
    return list;
  }, [sections]);

  const rapidClickCountRef = useRef<number>(0);

  const getRouteIndex = (path: string) => {
    const p = path.toLowerCase();
    const idx = menuItems.indexOf(p);
    if (idx !== -1) return idx;
    return menuItems.findIndex(item => p.startsWith(item) || item.startsWith(p));
  };

  // Troca de rota: snapshot da tela antiga + prepara a nova ANTES do slide.
  // Timer só depende de pathname (não cancela em re-render do outlet).
  useEffect(() => {
    const prevPath = lastPathname.current;
    const nextPath = location.pathname;
    if (prevPath === nextPath) return;

    lastPathname.current = nextPath;

    const skipPageSlide = true;

    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }

    if (skipPageSlide) {
      setTransitionQueue([]);
      transitionStartRef.current = 0;
      rapidClickCountRef.current = 0;
      setCurrentOutlet(outletRef.current);
      return;
    }

    const prevIndex = getRouteIndex(prevPath);
    const nextIndex = getRouteIndex(nextPath);

    let dir: 'forward' | 'backward' = 'forward';
    if (prevIndex !== -1 && nextIndex !== -1) {
      if (nextIndex > prevIndex) dir = 'forward';
      else if (nextIndex < prevIndex) dir = 'backward';
    }

    const now = Date.now();
    let calculatedDuration = 2550;

    const isTransitionActive =
      transitionQueueRef.current.length > 0 && transitionStartRef.current > 0;
    if (isTransitionActive) {
      rapidClickCountRef.current += 1;
    } else {
      rapidClickCountRef.current = 1;
    }

    if (isTransitionActive && rapidClickCountRef.current >= 3) {
      const elapsed = now - transitionStartRef.current;
      const remaining = 2550 - elapsed;
      calculatedDuration = Math.max(350, remaining);
    }

    transitionStartRef.current = now;

    // Congela a tela atual (antes) e monta a nova no painel live (durante o slide).
    const snapshotPrev = currentOutletRef.current || outletRef.current;
    const snapshotNext = outletRef.current;

    setTransitionQueue([
      {
        id: prevPath + '-' + now + '-prev',
        pathname: prevPath,
        outlet: snapshotPrev,
        state: 'exiting',
        direction: dir,
        durationMs: calculatedDuration,
      },
      {
        id: nextPath + '-' + now + '-curr',
        pathname: nextPath,
        outlet: snapshotNext,
        state: 'entering',
        direction: dir,
        durationMs: calculatedDuration,
      },
    ]);
    setCurrentOutlet(snapshotNext);

    transitionTimerRef.current = setTimeout(() => {
      // Só remove o overlay de saída — o painel live permanece montado (sem remount/flicker).
      setTransitionQueue([]);
      transitionStartRef.current = 0;
      rapidClickCountRef.current = 0;
      transitionTimerRef.current = null;
    }, calculatedDuration);

    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, [location.pathname]);

  // Atualiza o outlet live sem derrubar o timer da animação.
  useEffect(() => {
    setCurrentOutlet(outlet);
    setTransitionQueue((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((s) =>
        s.state === 'entering' ? { ...s, outlet } : s,
      );
    });
  }, [outlet]);

  // Screen Wake Lock para manter a tela sempre ativa (PWA)
  const { supported: wakeLockSupported, active: wakeLockActive, requestWakeLock, releaseWakeLock } = useScreenWakeLock();

  const handleToggleWakeLock = async () => {
    // Destrava o contexto de áudio em qualquer interação com este botão
    void audioAlerts.unlock();

    if (wakeLockActive) {
      await releaseWakeLock();
      import('sonner').then(m => m.toast.info('Modo tela ativa desativado.'));
    } else {
      const ok = await requestWakeLock();
      if (ok) {
        import('sonner').then(m => m.toast.success('Modo tela ativa habilitado com sucesso!'));
      } else {
        import('sonner').then(m => m.toast.error('Este dispositivo ou navegador não suporta tela ativa.'));
      }
    }
  };

  // Estados para verificação de email a partir do header
  const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerifyCode, setEmailVerifyCode] = useState('');
  const [emailVerifyLoading, setEmailVerifyLoading] = useState(false);
  const [emailVerifyError, setEmailVerifyError] = useState('');
  const [emailVerifyErrorDetails, setEmailVerifyErrorDetails] = useState('');
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [errorCopied, setErrorCopied] = useState(false);
  const [emailVerifySuccess, setEmailVerifySuccess] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [emailSendAttempts, setEmailSendAttempts] = useState(0);

  const [showPairingModal, setShowPairingModal] = useState(false);

  const [showThemeModal, setShowThemeModal] = useState(false);
  const [activeThemeTab, setActiveThemeTab] = useState<'presets' | 'advanced'>('presets');
  const [themeSettings, setThemeSettings] = useState(() => {
    const defaultSettings = {
      name: 'Aurora Boreal',
      primaryStart: '#4f46e5',
      primaryEnd: '#7c3aed',
      primary: '#6366f1',
      background: '#f8fafc',
      sidebar: '#0f172a',
      card: '#ffffff',
      fontSize: '14',
      fontSizeSidebar: '14',
      fontSizeHeader: '14',
      fontSizeBody: '14'
    };
    try {
      const saved = localStorage.getItem('ac_custom_theme');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultSettings,
          ...parsed,
          fontSizeSidebar: parsed.fontSizeSidebar || parsed.fontSize || '14',
          fontSizeHeader: parsed.fontSizeHeader || parsed.fontSize || '14',
          fontSizeBody: parsed.fontSizeBody || parsed.fontSize || '14',
        };
      }
    } catch { }
    return defaultSettings;
  });

  const THEME_PRESETS = [
    { 
      name: 'Aurora Boreal', 
      primaryStart: '#4f46e5', 
      primaryEnd: '#7c3aed', 
      primary: '#6366f1', 
      background: '#f8fafc', 
      sidebar: '#0f172a', 
      card: '#ffffff', 
      fontSize: '14',
      fontSizeSidebar: '14',
      fontSizeHeader: '14',
      fontSizeBody: '14'
    },
    { 
      name: 'Oceano Profundo', 
      primaryStart: '#1d4ed8', 
      primaryEnd: '#0d9488', 
      primary: '#2563eb', 
      background: '#f0fdfa', 
      sidebar: '#0b1e36', 
      card: '#ffffff', 
      fontSize: '14',
      fontSizeSidebar: '14',
      fontSizeHeader: '14',
      fontSizeBody: '14'
    },
    { 
      name: 'Pôr do Sol', 
      primaryStart: '#f97316', 
      primaryEnd: '#ec4899', 
      primary: '#f43f5e', 
      background: '#fff1f2', 
      sidebar: '#1c1917', 
      card: '#ffffff', 
      fontSize: '14',
      fontSizeSidebar: '14',
      fontSizeHeader: '14',
      fontSizeBody: '14'
    },
    { 
      name: 'Esmeralda Premium', 
      primaryStart: '#059669', 
      primaryEnd: '#047857', 
      primary: '#10b981', 
      background: '#f0fdf4', 
      sidebar: '#064e3b', 
      card: '#ffffff', 
      fontSize: '14',
      fontSizeSidebar: '14',
      fontSizeHeader: '14',
      fontSizeBody: '14'
    }
  ];

  const applyThemeCss = (theme: typeof themeSettings) => {
    let styleEl = document.getElementById('ac-theme-overrides');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'ac-theme-overrides';
      document.head.appendChild(styleEl);
    }

    const isDarkHex = (hex: string) => {
      if (!hex || hex[0] !== '#') return false;
      const c = hex.substring(1);
      if (c.length !== 3 && c.length !== 6) return false;
      const r = parseInt(c.length === 3 ? c[0] + c[0] : c.substring(0, 2), 16);
      const g = parseInt(c.length === 3 ? c[1] + c[1] : c.substring(2, 4), 16);
      const b = parseInt(c.length === 3 ? c[2] + c[2] : c.substring(4, 6), 16);
      if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luma < 140;
    };

    const isDark = isDarkHex(theme.card);

    const scrollbarStyles = `
      /* Estilização dinâmica das barras de rolagem seguindo o tema */
      ::-webkit-scrollbar {
        width: 8px !important;
        height: 8px !important;
      }
      ::-webkit-scrollbar-track {
        background: ${theme.background} !important;
      }
      ::-webkit-scrollbar-thumb {
        background: ${isDark ? '#3f3f46' : '#cbd5e1'} !important;
        border-radius: 99px !important;
      }
      ::-webkit-scrollbar-thumb:hover {
        background: ${isDark ? '#52525b' : '#94a3b8'} !important;
      }
      /* Firefox */
      * {
        scrollbar-width: thin !important;
        scrollbar-color: ${isDark ? '#3f3f46 #18181b' : '#cbd5e1 #f1f5f9'} !important;
      }
    `;

    const darkOverrides = isDark ? `
      /* Ajuste de contraste para cards escuros e textos */
      *[style*="background: white"], 
      *[style*="background: #ffffff"],
      *[style*="background:#ffffff"],
      *[style*="backgroundColor: white"], 
      *[style*="backgroundColor: #ffffff"],
      *[style*="backgroundColor:#ffffff"],
      *[style*="background-color: white"],
      *[style*="background-color: #ffffff"],
      *[style*="background-color:#ffffff"],
      .bg-white:not(header), .bg-card:not(header):not(.ac-env-header), .modal-content {
        background-color: var(--card-override) !important;
        background: var(--card-override) !important;
        color: #f4f4f5 !important;
        border-color: rgba(255, 255, 255, 0.08) !important;
      }
      
      /* Substituição de cores de textos cinza/escuro para claro somente dentro de cards escuros */
      .bg-white .text-slate-800, .bg-white .text-slate-900, .bg-white .text-slate-700, .bg-white .text-slate-950,
      .bg-card .text-slate-800, .bg-card .text-slate-900, .bg-card .text-slate-700, .bg-card .text-slate-950,
      .modal-content .text-slate-800, .modal-content .text-slate-900, .modal-content .text-slate-700,
      *[style*="background-color: var(--card-override)"] .text-slate-800,
      *[style*="background-color: var(--card-override)"] .text-slate-900,
      *[style*="background: var(--card-override)"] .text-slate-800,
      *[style*="background: var(--card-override)"] .text-slate-900,
      .text-slate-800, .text-slate-900, .text-slate-700, .text-slate-950,
      .text-gray-800, .text-gray-900, .text-gray-700 {
        color: #e4e4e7 !important;
      }
      
      .bg-white .text-slate-600, .bg-white .text-slate-500,
      .bg-card .text-slate-600, .bg-card .text-slate-500,
      .text-slate-600, .text-slate-500, .text-gray-600, .text-gray-500, .text-muted-foreground {
        color: #a1a1aa !important;
      }
      
      /* Cabeçalhos, células e labels ficam claros somente quando dentro de containers escuros */
      .bg-white h1, .bg-card h1, .bg-white h2, .bg-card h2,
      .bg-white h3, .bg-card h3, .bg-white h4, .bg-card h4,
      .bg-white h5, .bg-card h5, .bg-white h6, .bg-card h6,
      .bg-white th, .bg-card th, .bg-white td, .bg-card td,
      .bg-white label, .bg-card label,
      .modal-content h1, .modal-content h2, .modal-content h3, .modal-content h4,
      .modal-content th, .modal-content td, .modal-content label,
      *[style*="background-color: var(--card-override)"] h1,
      *[style*="background-color: var(--card-override)"] h2,
      *[style*="background-color: var(--card-override)"] h3,
      *[style*="background-color: var(--card-override)"] h4,
      *[style*="background-color: var(--card-override)"] th,
      *[style*="background-color: var(--card-override)"] td,
      *[style*="background-color: var(--card-override)"] label,
      *[style*="background: var(--card-override)"] th,
      *[style*="background: var(--card-override)"] td,
      *[style*="background: var(--card-override)"] label {
        color: #ffffff !important;
      }
      
      .border, .border-slate-100, .border-slate-200, .border-gray-200, .border-slate-300 {
        border-color: rgba(255, 255, 255, 0.08) !important;
      }
      
      /* Calendários e grids com inline styles */
      *[style*="color: rgb(30, 41, 59)"] {
        color: #ffffff !important;
      }
      *[style*="border: 1px solid rgb(226, 232, 240)"] {
        border-color: rgba(255, 255, 255, 0.08) !important;
      }
    ` : '';

    styleEl.innerHTML = `
      :root {
        --primary-gradient-start: ${theme.primaryStart || theme.primary};
        --primary-gradient-end: ${theme.primaryEnd || theme.primary};
        --primary-override: ${theme.primary};
        --bg-override: ${theme.background};
        --sidebar-override: ${theme.sidebar};
        --card-override: ${theme.card};
        --sidebar-font-size-override: ${theme.fontSizeSidebar || theme.fontSize}px;
        --header-font-size-override: ${theme.fontSizeHeader || theme.fontSize}px;
        --body-font-size-override: ${theme.fontSizeBody || theme.fontSize}px;
      }
      body, main, .bg-background, .alu-desk-host {
        background-color: var(--bg-override) !important;
      }
      aside.ac-layout-sidebar, .ac-sidebar-bg, [style*="boxShadow: 2px 0 10px"] {
        background-color: var(--sidebar-override) !important;
      }
      /* Protege o cabeçalho com environment (.ac-env-header) das sobreposições de background do tema */
      .bg-white:not(header), .bg-card:not(header):not(.ac-env-header), .modal-content {
        background-color: var(--card-override) !important;
      }
      /* Adaptação de checkboxes para evitar que pareçam marcados */
      input[type="checkbox"] {
        accent-color: var(--primary-override) !important;
        background-color: var(--card-override) !important;
        border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'} !important;
      }
      .bg-primary, .bg-indigo-600, .bg-violet-600, .bg-indigo-500, .bg-violet-500, [style*="background: linear-gradient(135deg, #7c3aed"], .btn-save, .btn-primary {
        background: linear-gradient(135deg, var(--primary-gradient-start), var(--primary-gradient-end)) !important;
        background-color: var(--primary-gradient-start) !important;
      }
      .text-primary, .text-indigo-600, .text-violet-600, .text-indigo-700, .text-violet-700 {
        color: var(--primary-gradient-start) !important;
      }

      /* Sidebar font override */
      aside.ac-layout-sidebar,
      aside.ac-layout-sidebar a,
      aside.ac-layout-sidebar span,
      aside.ac-layout-sidebar button,
      aside.ac-layout-sidebar .text-xs,
      aside.ac-layout-sidebar .text-sm,
      aside.ac-layout-sidebar .text-base {
        font-size: var(--sidebar-font-size-override) !important;
      }
      
      /* Header font override */
      header.ac-env-header,
      header.ac-env-header a,
      header.ac-env-header span,
      header.ac-env-header button,
      header.ac-env-header div:not(.flex):not(.relative),
      header.ac-env-header .text-xs,
      header.ac-env-header .text-sm,
      header.ac-env-header .text-base {
        font-size: var(--header-font-size-override) !important;
      }

      /* Body font override */
      main,
      main p,
      main span,
      main td,
      main th,
      main a,
      main button,
      main label,
      main input,
      main select,
      main textarea,
      main .text-sm,
      main .text-xs,
      main .text-base {
        font-size: var(--body-font-size-override) !important;
      }

      /* Body headers proportional scaling */
      main h1 {
        font-size: calc(var(--body-font-size-override) * 1.6) !important;
      }
      main h2 {
        font-size: calc(var(--body-font-size-override) * 1.35) !important;
      }
      main h3 {
        font-size: calc(var(--body-font-size-override) * 1.18) !important;
      }
      main h4 {
        font-size: calc(var(--body-font-size-override) * 1.08) !important;
      }

      ${scrollbarStyles}
      ${darkOverrides}
    `;
  };

  useEffect(() => {
    applyThemeCss(themeSettings);
  }, [themeSettings]);



  const emailCodeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (emailResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setEmailResendCooldown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [emailResendCooldown]);

  useEffect(() => {
    if (showEmailVerifyModal) {
      const t = setTimeout(() => {
        emailCodeInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [showEmailVerifyModal]);

  const extractApiErrorMessage = (err: any, fallback = 'Erro ao processar solicitação'): string => {
    const data = err?.response?.data;
    if (typeof data?.error?.message === 'string' && data.error.message.trim()) return data.error.message;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    if (Array.isArray(data?.message) && data.message.length > 0) return data.message.join(', ');
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
    if (typeof err?.message === 'string' && err.message.trim()) return err.message;
    return fallback;
  };

  const handleTriggerEmailVerification = async () => {
    if (emailResendCooldown > 0) {
      toast.info(`Aguarde ${emailResendCooldown} segundos antes de solicitar novo código.`);
      return;
    }
    setEmailVerifyLoading(true);
    setEmailVerifyError('');
    setEmailVerifyErrorDetails('');
    setShowErrorDetails(false);
    setEmailVerifySuccess(false);
    setEmailSendAttempts(prev => prev + 1);
    try {
      await api.post('/auth/me/send-verification-email');
      setEmailCodeSent(true); // Entra na tela de digitação do código!
      setEmailResendCooldown(60); // 60 segundos de cooldown anti-abuso
      toast.info('Código de verificação enviado para o seu e-mail!');
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, 'Erro ao enviar e-mail de verificação.');
      setEmailVerifyError(msg);
      const details = {
        timestamp: new Date().toISOString(),
        endpoint: '/auth/me/send-verification-email',
        status: err.response?.status || 'CLIENT_OR_NETWORK_ERROR',
        statusText: err.response?.statusText || '',
        message: msg,
        targetEmail: user?.email || '',
        attempts: emailSendAttempts + 1,
        responseData: err.response?.data || null,
      };
      setEmailVerifyErrorDetails(JSON.stringify(details, null, 2));
    } finally {
      setEmailVerifyLoading(false);
    }
  };

  useEffect(() => {
    const handleOpenModal = () => {
      setShowEmailVerifyModal(true);
      setEmailVerifyError('');
      setEmailVerifySuccess(false);
    };
    window.addEventListener('ac:email:verify:open', handleOpenModal);
    return () => window.removeEventListener('ac:email:verify:open', handleOpenModal);
  }, []);

  const handleConfirmEmailVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailVerifyCode.trim() || emailVerifyCode.length < 6) {
      setEmailVerifyError('Digite o código de 6 dígitos.');
      return;
    }
    setEmailVerifyLoading(true);
    setEmailVerifyError('');
    try {
      await api.post('/auth/me/verify-email', { codigo: emailVerifyCode.trim() });
      setEmailVerifySuccess(true);
      // Atualiza o estado do usuário localmente e no localStorage
      if (user) {
        const updatedUser = { ...user, emailVerificado: true };
        useAuthStore.setState({ user: updatedUser });
        try {
          localStorage.setItem('ac_user', JSON.stringify(updatedUser));
        } catch {}
      }
      window.dispatchEvent(new CustomEvent('ac:email:verified'));
      toast.success('E-mail verificado com sucesso!');
      setTimeout(() => {
        setShowEmailVerifyModal(false);
        setEmailVerifyCode('');
      }, 1500);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Código incorreto ou expirado.';
      setEmailVerifyError(msg);
      const details = {
        timestamp: new Date().toISOString(),
        endpoint: '/auth/me/verify-email',
        status: err.response?.status || 400,
        statusText: err.response?.statusText || '',
        message: msg,
        targetEmail: user?.email || '',
        sentCode: emailVerifyCode.trim(),
        responseData: err.response?.data || null,
      };
      setEmailVerifyErrorDetails(JSON.stringify(details, null, 2));
    } finally {
      setEmailVerifyLoading(false);
    }
  };

  // Estados para o RESTART_DB (ADMIN — inclusive produção, com backup)
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [restartAdmins, setRestartAdmins] = useState<Array<{ id: string; nome: string; email: string; checked: boolean }>>([]);
  const [restartPassword, setRestartPassword] = useState('');
  const [restartLoading, setRestartLoading] = useState(false);
  const [restartDateError, setRestartDateError] = useState('');
  const [restartProgress, setRestartProgress] = useState<{
    open: boolean;
    jobId?: string;
    progress: number;
    step: string;
    message: string;
    status: 'EM_ANDAMENTO' | 'CONCLUIDO' | 'ERRO' | 'IDLE';
    error?: string;
  }>({ open: false, progress: 0, step: '', message: '', status: 'IDLE' });

  const handleOpenRestartModal = async () => {
    setRestartPassword('');
    setRestartDateError('');
    setRestartAdmins([]);
    setShowRestartModal(true);
    setRestartLoading(true);
    try {
      const { data } = await api.get('/admin/admins');
      setRestartAdmins((data || []).map((u: any) => ({ ...u, checked: true })));
    } catch (err: any) {
      setRestartDateError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err.message ||
          'Erro ao carregar administradores.',
      );
    } finally {
      setRestartLoading(false);
    }
  };

  const handleToggleAdminCheck = (id: string) => {
    setRestartAdmins(prev => prev.map(a => a.id === id ? { ...a, checked: !a.checked } : a));
  };

  const handleConfirmRestart = async () => {
    if (restartPassword !== 'marcelo') {
      setRestartDateError('Senha de confirmação inválida.');
      return;
    }
    setRestartLoading(true);
    setRestartDateError('');
    const emailsPreservados = restartAdmins.filter(a => a.checked).map(a => a.email);

    try {
      const { data } = await api.post('/admin/restart-db', {
        emailsPreservados,
        senhaConfirmacao: restartPassword,
      });
      const jobId = data?.jobId as string;
      if (!jobId) {
        throw new Error('Job de reinício não retornou identificador.');
      }
      setShowRestartModal(false);
      setRestartProgress({
        open: true,
        jobId,
        progress: 5,
        step: 'FILA',
        message: 'Reinício iniciado. Aguarde — não feche esta janela…',
        status: 'EM_ANDAMENTO',
      });

      for (let i = 0; i < 180; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: st } = await api.get(`/admin/restart-db/status/${jobId}`);
          setRestartProgress((prev) => ({
            ...prev,
            open: true,
            jobId,
            progress: typeof st?.progress === 'number' ? st.progress : prev.progress,
            step: st?.step || prev.step,
            message: st?.message || prev.message,
            status: st?.status || prev.status,
            error: st?.error,
          }));
          if (st?.status === 'CONCLUIDO') {
            setTimeout(() => {
              logout();
              window.location.href = '/login?db_reset=1';
            }, 1800);
            return;
          }
          if (st?.status === 'ERRO') {
            setRestartLoading(false);
            return;
          }
        } catch {
          // rede instável durante o wipe — continua polling
        }
      }
      setRestartProgress((prev) => ({
        ...prev,
        status: 'ERRO',
        error: 'Tempo esgotado aguardando o reinício. Verifique os logs do Cloud Run.',
        progress: 100,
      }));
    } catch (err: any) {
      setRestartDateError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err.message ||
          'Erro ao reiniciar o banco.',
      );
      setRestartLoading(false);
    }
  };
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    'PAINEL DO USUÁRIO': false,
    'Gestão': false,
    'Comercial': true,
    'Operações': true,
    'RFID': true,
    'Suporte': true,
    'Produtividade': true,
    'Conteúdo': false,
    'Sistema': false, // Expandido por padrão a pedido do usuário
    'Inteligência': true,
    'Financeiro': true,
    'DESATIVADOS -': true,
  });
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const isAdmin = user?.perfil === 'ADMIN';
  const equipePathBlocked = useMemo(() => {
    if (!user || user.perfil !== 'ESCOLA' || !location.pathname.startsWith('/equipe')) {
      return { block: false as const, redirect: null as string | null, noAccess: false };
    }
    if (canAccessEquipePath(location.pathname, user)) {
      return { block: false as const, redirect: null, noAccess: false };
    }
    const redirect = getFirstAllowedEquipePath(user);
    return { block: true as const, redirect, noAccess: !redirect };
  }, [user, location.pathname]);

  const setupRouteRedirect = useMemo(() => {
    if (!setupGuard?.etapas?.length) return null;
    for (const [prefix, etapaId] of Object.entries(ADMIN_SETUP_PATH_TO_ETAPA)) {
      if (!location.pathname.startsWith(prefix)) continue;
      const etapa = setupGuard.etapas.find((e) => e.id === etapaId);
      if (etapa?.status === 'bloqueada') {
        const proxima = setupGuard.etapas.find(
          (e) => e.status === 'pendente' || e.status === 'parcial',
        );
        return proxima?.rota ?? '/admin/dashboard';
      }
      return null;
    }
    return null;
  }, [setupGuard, location.pathname]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [chatOpen, setChatOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('ac_chat_open') === 'true';
    }
    return false;
  });

  const [isDocked, setIsDocked] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('ac_chat_docked');
      return stored === null ? true : stored === 'true';
    }
    return true;
  });

  const toggleDock = () => {
    setIsDocked((prev) => {
      const next = !prev;
      localStorage.setItem('ac_chat_docked', String(next));
      return next;
    });
  };

  // Persiste a visibilidade do chat no localStorage
  useEffect(() => {
    localStorage.setItem('ac_chat_open', String(chatOpen));
  }, [chatOpen]);

  // Integração global: widgets de Equipe/Briefing/Alertas abrem o Chat Interno.
  useEffect(() => {
    const handleOpenChat = (event: Event) => {
      const detail = (event as CustomEvent<{ keepTeamOpen?: boolean }>).detail || {};
      setChatOpen(true);
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('ac:chat:hydrate', { detail }));
      }, 0);
    };

    window.addEventListener('ac:chat:open', handleOpenChat as EventListener);
    return () => window.removeEventListener('ac:chat:open', handleOpenChat as EventListener);
  }, []);

  const fetchChatUnreadCount = async () => {
    if (!isAuthenticated || !user) return;
    if (document.visibilityState !== 'visible') return;
    try {
      const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
      if (!jwtToken) return;

      const res = await fetch(`${API}/chat-interno/conversas`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          const totalUnread = data.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
          setChatUnreadCount(totalUnread);
        }
      }
    } catch (e) {
      console.warn('[BaseLayout] Erro ao buscar mensagens não lidas do chat:', e);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchChatUnreadCount();
    const interval = setInterval(fetchChatUnreadCount, 60000); // 60s
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const pingPresenca = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        await api.post('/feedback/presenca', { rota: location.pathname });
      } catch (err) {
        console.warn('[BaseLayout] Erro ao registrar presença:', err);
      }
    };

    pingPresenca();
    const interval = setInterval(pingPresenca, 30000); // 30s
    return () => clearInterval(interval);
  }, [isAuthenticated, location.pathname]);
  const darkMode = useUiStore((s) => s.darkMode);
  const sidebarExpanded = useUiStore((s) => s.sidebarExpanded);
  const toggleSidebarExpanded = useUiStore((s) => s.toggleSidebarExpanded);
  const setSidebarExpanded = useUiStore((s) => s.setSidebarExpanded);
  const chamadaAtiva = useChamadaStore((s) => s.chamadaEmAndamento);

  // Breadcrumb dinâmico
  const currentPage = useMemo(() => {
    for (const s of sections) {
      for (const item of s.items) {
        if (location.pathname === item.to || location.pathname.startsWith(item.to + '/')) {
          return { section: s.title, label: item.label, icon: item.icon };
        }
      }
    }
    return { section: null, label: headerTitle, icon: null };
  }, [location.pathname, sections, headerTitle]);

  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [selectedZoomImage, setSelectedZoomImage] = useState<string | null>(null);
  const [isRecordingActive, setIsRecordingActive] = useState(() => {
    return typeof window !== 'undefined' && sessionStorage.getItem('ac_recording_active') === 'true';
  });
  const [showRedHeader, setShowRedHeader] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackDesc, setFeedbackDesc] = useState('');
  const [isMockRecordingAudio, setIsMockRecordingAudio] = useState(false);
  const [mockAudioDuration, setMockAudioDuration] = useState(0);
  const [hasAudio, setHasAudio] = useState(false);
  const [iaGenerating, setIaGenerating] = useState(false);

  const captureMockScreenshot = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const grad = ctx.createLinearGradient(0, 0, 640, 360);
      grad.addColorStop(0, '#1e1b4b');
      grad.addColorStop(1, '#311042');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 640, 360);

      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.fillRect(15, 15, 120, 330);
      ctx.fillRect(150, 15, 475, 50);

      ctx.fillStyle = '#7c3aed';
      ctx.fillRect(170, 90, 200, 100);
      ctx.fillStyle = '#0ea5e9';
      ctx.fillRect(390, 90, 215, 100);
      ctx.fillStyle = '#10b981';
      ctx.fillRect(170, 210, 435, 110);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText(`Captura: ${currentPage?.label || 'Aluno Conectado'}`, 180, 45);
      ctx.fillStyle = '#a78bfa';
      ctx.font = '12px sans-serif';
      ctx.fillText(`Perfil: ${user?.perfil || 'Usuário'} · Rota: ${location.pathname}`, 180, 120);
      ctx.fillStyle = '#94a3b8';
      ctx.fillText(`Data: ${new Date().toLocaleString('pt-BR')}`, 180, 140);

      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(320, 180, 60, 0, Math.PI * 2);
      ctx.stroke();

      const flashDiv = document.createElement('div');
      flashDiv.style.position = 'fixed';
      flashDiv.style.inset = '0';
      flashDiv.style.background = '#ffffff';
      flashDiv.style.opacity = '0.9';
      flashDiv.style.zIndex = '999999';
      flashDiv.style.transition = 'opacity 0.25s ease-out';
      flashDiv.style.pointerEvents = 'none';
      document.body.appendChild(flashDiv);
      setTimeout(() => {
        flashDiv.style.opacity = '0';
        setTimeout(() => flashDiv.remove(), 250);
      }, 50);

      const dataUrl = canvas.toDataURL('image/png');
      setScreenshots(prev => [...prev, dataUrl]);
      import('sonner').then(m => m.toast.success('Captura de tela registrada! (Use Ctrl+PrintScreen ou Ctrl+Shift+S)'));
    }
  };

  useEffect(() => {
    const handleScreenshotKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isInput) return;

      const isPrtScn = e.key === 'PrintScreen';
      const isCtrlPrtScn = e.key === 'PrintScreen' && e.ctrlKey;
      const isCtrlShiftS = e.ctrlKey && e.shiftKey && (e.key === 'S' || e.key === 's');

      if (isPrtScn || isCtrlPrtScn || isCtrlShiftS) {
        e.preventDefault();
        captureMockScreenshot();
      } else if (e.key === 'F4') {
        if (window.self !== window.top) {
          // Se estiver rodando dentro do iframe do gravador (modo gravação),
          // não interceptamos o F4 e deixamos o tratador do parent agir como stop.
          return;
        }
        e.preventDefault();
        if (isRecordingActive) {
          // Envia evento para parar gravação ativa
          window.dispatchEvent(new CustomEvent('ac-recording-state-change', { detail: { active: false } }));
          console.log('[BaseLayout] F4: Parando gravação ativa.');
        } else {
          const url = `/master/recorder?url=${encodeURIComponent(location.pathname + location.search)}&mode=feedback`;
          window.open(url, '_blank');
          console.log('[BaseLayout] F4: Iniciando gravador de feedback.');
        }
      }
    };
    window.addEventListener('keydown', handleScreenshotKey);
    return () => window.removeEventListener('keydown', handleScreenshotKey);
  }, [currentPage?.label, user, location.pathname, location.search]);

  useEffect(() => {
    const handleStateChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const active = !!customEvent.detail?.active;
      setIsRecordingActive(active);
      if (!active) {
        setShowRedHeader(false);
      }
    };
    window.addEventListener('ac-recording-state-change', handleStateChange);
    return () => window.removeEventListener('ac-recording-state-change', handleStateChange);
  }, []);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 3200);
    return () => clearTimeout(timer);
  }, []);

  // ── Push Notifications ──
  const [notifState, setNotifState] = useState<'idle' | 'ask' | 'waiting' | 'success' | 'active'>(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('ac_push_active') === 'true') return 'active';
    return 'idle';
  });
  const [notifLoading, setNotifLoading] = useState(false);
  const notifButtonRef = useRef<HTMLButtonElement>(null);
  const notifMenuRef = useRef<HTMLDivElement>(null);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>(() => {
    return typeof Notification !== 'undefined' ? Notification.permission : 'default';
  });
  const isPushNotActive = permissionStatus !== 'granted' && !user?.fcmToken && (typeof window !== 'undefined' && localStorage.getItem('ac_push_active') !== 'true');
  const needsPush = typeof Notification !== 'undefined' && Notification.permission !== 'granted' && !user?.fcmToken && localStorage.getItem('ac_push_active') !== 'true';

  const handleActivatePush = async () => {
    setNotifLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      if (permission !== 'granted') {
        setNotifState('idle');
        setNotifLoading(false);
        return;
      }
      setNotifState('waiting' as any);
      const { initializeApp, getApps } = await import('firebase/app');
      const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
      const cfg = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      };
      const app = getApps().length ? getApps()[0] : initializeApp(cfg);
      const messaging = getMessaging(app);
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;
      const fcmToken = await getToken(messaging, { vapidKey });
      onMessage(messaging, (payload) => {
        if (payload.notification) {
          new Notification(payload.notification.title || 'Aluno Conectado', {
            body: payload.notification.body || '', icon: '/icons/icon-192x192.png',
          });
        }
      });
      const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
      await registerAuthenticatedPushToken(
        { fcmToken, plataforma: 'WEB', tipo: 'desktop' },
        { bearerToken: jwtToken },
      );
      localStorage.setItem('ac_push_active', 'true');
      setNotifState('active');
      setTimeout(() => setNotifState('idle'), 3000);
    } catch (err) {
      console.error('[BaseLayout] Erro ao ativar push:', err);
      setNotifState('idle');
    } finally {
      setNotifLoading(false);
    }
  };

  // Auto-ativação suave de push se permissão do navegador já estiver concedida
  useEffect(() => {
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted' &&
      !user?.fcmToken &&
      localStorage.getItem('ac_push_active') !== 'true'
    ) {
      handleActivatePush().catch(() => {});
    }
  }, [user?.fcmToken]);

  // Notificações In-App de Comunicados
  const [notifTab, setNotifTab] = useState<'comunicados' | 'seguranca'>('comunicados');
  const [recentComunicados, setRecentComunicados] = useState<any[]>([]);
  const [visualizandoComunicado, setVisualizandoComunicado] = useState<any | null>(null);
  const [readComunicadoIds, setReadComunicadoIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('ac_read_comunicados') || '[]');
    } catch {
      return [];
    }
  });

  const fetchRecentComunicados = async () => {
    if (!isAuthenticated) return;
    try {
      let endpoint = '';
      if (user?.escolaId) {
        endpoint = `/escolas/${user.escolaId}/comunicados`;
      } else if (user?.municipioId) {
        endpoint = `/municipios/${user.municipioId}/comunicados`;
      } else if (user?.perfil === 'ADMIN') {
        endpoint = '/master/comunicados';
      } else if (user?.perfil === 'RESPONSAVEL') {
        endpoint = '/mobile/responsavel/comunicados';
      } else if (user?.perfil === 'PROFESSOR') {
        endpoint = user?.escolaId ? `/escolas/${user.escolaId}/comunicados` : '';
      }

      if (!endpoint) return;
      const res = await api.get(endpoint);
      const lista = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
      if (lista.length > 0) {
        const filtered = lista.filter((c: any) => {
          // 1. Nunca recebe comunicados criados pelo próprio usuário logado
          if (c.criadoPorId && user?.id && c.criadoPorId === user.id) return false;
          if (c.criadoPor?.id && user?.id && c.criadoPor.id === user.id) return false;

          // 2. Se for MUNICIPAL, não recebe os comunicados municipais emitidos para a rede, a não ser que venham de instância superior (ESTADO, REDE, MASTER)
          if (user?.perfil === 'MUNICIPAL') {
            const isOrigemSuperior =
              c.tipoEmissor === 'ESTADO' ||
              c.tipoEmissor === 'REDE' ||
              c.tipoEmissor === 'ADMIN' ||
              c.origem === 'ESTADUAL' ||
              c.origem === 'REDE' ||
              c.origem === 'MASTER' ||
              c.origem === 'ADMIN' ||
              Boolean(c.origemMaster) ||
              Boolean(c.origemEstadual);

            if (!isOrigemSuperior) {
              return false;
            }
          }

          // 3. Se for ESCOLA, não recebe comunicados criados pela própria escola para seus alunos/pais
          if (user?.perfil === 'ESCOLA' && c.escolaId === user?.escolaId && c.criadoPorId === user?.id) {
            return false;
          }

          return true;
        });

        // Sincroniza IDs já lidos no servidor para blindar contra reaparecimento em novo navegador
        const serverReadIds = filtered
          .filter(
            (c: any) =>
              c.visualizado ||
              c.lido ||
              c.visualizadoEm ||
              (Array.isArray(c.visualizacoes) && c.visualizacoes.length > 0),
          )
          .map((c: any) => c.id);

        if (serverReadIds.length > 0) {
          setReadComunicadoIds((prev) => {
            const merged = Array.from(new Set([...prev, ...serverReadIds]));
            try {
              localStorage.setItem('ac_read_comunicados', JSON.stringify(merged));
            } catch {}
            return merged;
          });
        }

        setRecentComunicados(filtered.slice(0, 30));
      }
    } catch {
      // Ignora erro
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchRecentComunicados();
    const interval = setInterval(fetchRecentComunicados, 25000);
    const onPush = () => fetchRecentComunicados();
    window.addEventListener('ac-push-received', onPush);

    const handleSyncRead = (e: any) => {
      const id = e.detail?.id;
      const all = e.detail?.all;
      const ids = e.detail?.ids;
      if (all && ids) {
        setReadComunicadoIds((prev) => Array.from(new Set([...prev, ...ids])));
        setRecentComunicados((prev) => prev.map((c) => ({ ...c, lido: true, visualizado: true })));
      } else if (id) {
        setReadComunicadoIds((prev) => Array.from(new Set([...prev, id])));
        setRecentComunicados((prev) => prev.map((c) => (c.id === id ? { ...c, lido: true, visualizado: true } : c)));
      }
    };
    window.addEventListener('ac:comunicado:read', handleSyncRead);

    return () => {
      clearInterval(interval);
      window.removeEventListener('ac-push-received', onPush);
      window.removeEventListener('ac:comunicado:read', handleSyncRead);
    };
  }, [isAuthenticated, user?.id]);

  const unreadComunicados = useMemo(() => {
    return recentComunicados.filter(
      (c) =>
        c.ativo !== false &&
        !c.lido &&
        !c.visualizado &&
        !c.visualizadoEm &&
        !(Array.isArray(c.visualizacoes) && c.visualizacoes.length > 0) &&
        !readComunicadoIds.includes(c.id)
    );
  }, [recentComunicados, readComunicadoIds]);

  const markComunicadoAsRead = (id: string) => {
    const updated = Array.from(new Set([...readComunicadoIds, id]));
    setReadComunicadoIds(updated);
    setRecentComunicados((prev) => prev.map((c) => (c.id === id ? { ...c, lido: true, visualizado: true } : c)));
    try {
      localStorage.setItem('ac_read_comunicados', JSON.stringify(updated));
    } catch {}
    window.dispatchEvent(new CustomEvent('ac:comunicado:read', { detail: { id } }));
    api.post(`/comunicados/${id}/visualizar`).catch(() => {});
  };

  const markAllComunicadosAsRead = () => {
    const allIds = recentComunicados.map((c) => c.id);
    const updated = Array.from(new Set([...readComunicadoIds, ...allIds]));
    setReadComunicadoIds(updated);
    setRecentComunicados((prev) => prev.map((c) => ({ ...c, lido: true, visualizado: true })));
    try {
      localStorage.setItem('ac_read_comunicados', JSON.stringify(updated));
    } catch {}
    window.dispatchEvent(new CustomEvent('ac:comunicado:read', { detail: { all: true, ids: allIds } }));
    if (allIds.length > 0) {
      api.post('/comunicados/visualizar-lote', { ids: allIds }).catch(() => {});
    }
  };

  // Listener global para abrir o modal oficial de visualização de comunicados
  useEffect(() => {
    const handleOpenModal = async (e: any) => {
      const data = e.detail;
      if (!data) return;

      // Se já passou o objeto do comunicado
      if (data.id && (data.corpo || data.mensagem || data.titulo)) {
        setVisualizandoComunicado(data);
        markComunicadoAsRead(data.id);
        return;
      }

      // Se passou o ID
      const id = data.id || data.comunicadoId || data.notificacaoId;
      if (id) {
        const found = recentComunicados.find((c) => c.id === id);
        if (found) {
          setVisualizandoComunicado(found);
          markComunicadoAsRead(id);
          return;
        }

        try {
          const res = await api.get(`/comunicados/${id}`);
          if (res.data) {
            setVisualizandoComunicado(res.data);
            markComunicadoAsRead(id);
          }
        } catch {}
      }
    };

    window.addEventListener('ac:open:comunicado:modal', handleOpenModal);
    return () => window.removeEventListener('ac:open:comunicado:modal', handleOpenModal);
  }, [recentComunicados, readComunicadoIds]);

  // Deep-link: ?comunicadoId=... ou ?notificacaoId=... na URL (ex: clique no push notification)
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    const id = params.get('comunicadoId') || params.get('notificacaoId');
    if (!id) return;

    window.dispatchEvent(new CustomEvent('ac:open:comunicado:modal', { detail: { id } }));

    params.delete('comunicadoId');
    params.delete('notificacaoId');
    const newSearch = params.toString();
    navigate(location.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
  }, [location.search, isAuthenticated]);

  // Pendências / Solicitações de Acesso de Segurança
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [openedApprovalIds, setOpenedApprovalIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('ac_opened_approvals') || '[]');
    } catch {
      return [];
    }
  });

  // Sincronização de Dispositivos e Alertas de Inatividade (+30 dias)
  const [devicesList, setDevicesList] = useState<Dispositivo[]>(getUserDevices);
  useEffect(() => {
    const handleSync = () => setDevicesList(getUserDevices());
    window.addEventListener('ac:devices:updated', handleSync);
    return () => window.removeEventListener('ac:devices:updated', handleSync);
  }, []);
  const inactiveDevices = useMemo(() => getInactiveDevices(devicesList), [devicesList]);

  // Controle de exibição de grupos de notificações
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [hasOpenedList, setHasOpenedList] = useState(false);
  const [lastSeenCount, setLastSeenCount] = useState(0);

  const unreadApprovals = useMemo(() => {
    const cutoffTime = Date.now() - 24 * 60 * 60 * 1000; // 24 horas atrás
    return pendingApprovals.filter((ap) => {
      if (openedApprovalIds.includes(ap.id)) return false;

      const isExpired = ap.status === 'EXPIRADO' || new Date(ap.expiraEm).getTime() <= Date.now();

      // Ativas sempre exibe; vencidas apenas se criadas nas últimas 24h
      if (!isExpired) return true;
      return new Date(ap.criadoEm).getTime() >= cutoffTime;
    });
  }, [pendingApprovals, openedApprovalIds]);

  const activeUnreadApprovals = useMemo(() => {
    const loginTimeStr = sessionStorage.getItem('ac_login_time');
    const loginTime = loginTimeStr ? new Date(loginTimeStr).getTime() : Date.now();

    return unreadApprovals.filter((ap: any) => {
      const isPendente = ap.status === 'PENDENTE';
      const isNotExpired = new Date(ap.expiraEm).getTime() > Date.now();
      // Redirecionamento intrusivo automático na tela do celular só ocorre para solicitações geradas após o login
      const isAfterLogin = new Date(ap.criadoEm).getTime() >= loginTime;
      return isPendente && isNotExpired && isAfterLogin;
    });
  }, [unreadApprovals]);

  const groupedApprovals = useMemo(() => {
    const result: { type: 'single' | 'group'; id: string; items: any[]; isExpired: boolean }[] = [];
    const expiredGroupsMap = new Map<string, any[]>();

    for (const ap of unreadApprovals) {
      const isExpired = ap.status === 'EXPIRADO' || new Date(ap.expiraEm).getTime() <= Date.now();
      if (!isExpired) {
        // Solicitações ativas: individuais
        result.push({
          type: 'single',
          id: ap.id,
          items: [ap],
          isExpired: false
        });
      } else {
        // Solicitações vencidas: agrupadas por tipo
        const tipo = ap.tipo || 'OUTRO';
        if (!expiredGroupsMap.has(tipo)) {
          expiredGroupsMap.set(tipo, []);
        }
        expiredGroupsMap.get(tipo)!.push(ap);
      }
    }

    // Adiciona os grupos de expirados construídos
    expiredGroupsMap.forEach((items, tipo) => {
      result.push({
        type: 'group',
        id: `expired-group-${tipo}`,
        items,
        isExpired: true
      });
    });

    return result;
  }, [unreadApprovals]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  useEffect(() => {
    if (unreadApprovals.length > lastSeenCount) {
      setHasOpenedList(false);
    }
    setLastSeenCount(unreadApprovals.length);
  }, [unreadApprovals.length, lastSeenCount]);

  const clearAllNotifs = () => {
    setOpenedApprovalIds((prev) => {
      const next = [...prev];
      let changed = false;
      for (const ap of pendingApprovals) {
        if (!next.includes(ap.id)) {
          next.push(ap.id);
          changed = true;
        }
      }
      if (changed) {
        try {
          localStorage.setItem('ac_opened_approvals', JSON.stringify(next));
        } catch (e) {
          console.warn(e);
        }
      }
      return next;
    });
  };

  const hasExpiredUnread = useMemo(() => {
    return unreadApprovals.length > 0 && !hasOpenedList;
  }, [unreadApprovals, hasOpenedList]);

  const syncOpenedApprovals = () => {
    try {
      const ids = JSON.parse(localStorage.getItem('ac_opened_approvals') || '[]');
      setOpenedApprovalIds(ids);
    } catch { }
  };

  const markApprovalAsOpened = (id: string) => {
    setOpenedApprovalIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      try {
        localStorage.setItem('ac_opened_approvals', JSON.stringify(next));
      } catch (e) {
        console.warn(e);
      }
      return next;
    });
  };

  const fetchPendingApprovals = async () => {
    if (!isAuthenticated || !user) return;
    try {
      const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
      if (!jwtToken) return;

      const data = await listPendingApprovals({ bearerToken: jwtToken });
      setPendingApprovals(Array.isArray(data) ? data : []);
      syncOpenedApprovals();
    } catch (e) {
      console.warn('[BaseLayout] Erro ao buscar aprovações pendentes:', e);
    }
  };

  useEffect(() => {
    window.addEventListener('focus', syncOpenedApprovals);
    return () => window.removeEventListener('focus', syncOpenedApprovals);
  }, []);

  useEffect(() => {
    fetchPendingApprovals();
    const interval = setInterval(fetchPendingApprovals, 20000); // 20s
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  // Auto-redirect to active pending approvals (Google Prompt style) — SOMENTE MOBILE/PWA
  useEffect(() => {
    if (!isAuthenticated) return;

    // Detectar se é mobile/PWA — approval redirect só faz sentido no mobile
    const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
    if (!isMobile && !isStandalone) return; // Desktop: não redirecionar

    // Encontra a primeira aprovação não lida que ainda está ativa (PENDENTE e com expiração futura)
    const activeApproval = activeUnreadApprovals[0];

    if (activeApproval && !location.pathname.startsWith('/security/approve/')) {
      // Registrar imediatamente como aberta no estado e localStorage para evitar loops
      markApprovalAsOpened(activeApproval.id);

      // Navegar para a tela de aprovação
      navigate(`/security/approve/${activeApproval.id}`);
    }
  }, [activeUnreadApprovals, location.pathname, isAuthenticated, navigate]);

  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  // Atalho global Ctrl + F9 para abrir o Gravador em uma nova aba com a URL de contexto
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'F9') {
        e.preventDefault();
        const impersonating = isImpersonating || (typeof window !== 'undefined' && (!!sessionStorage.getItem('ac_impersonate') || !!sessionStorage.getItem('ac_impersonatedByName')));
        const hasAccess = user?.perfil === 'ADMIN' || (user?.perfil as string) === 'MASTER' || (user?.perfil as string) === 'SISTEMA' || impersonating;
        if (hasAccess) {
          const currentUrl = window.location.pathname + window.location.search;
          window.open(`/master/recorder?url=${encodeURIComponent(currentUrl)}`, '_blank');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImpersonating, user]);

  // Close user menu on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  // Close notif popup on click outside
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (notifButtonRef.current && notifButtonRef.current.contains(target)) {
        return;
      }
      if (notifMenuRef.current && notifMenuRef.current.contains(target)) {
        return;
      }
      if (notifState === 'ask') {
        setNotifState('idle');
      }
    };
    if (notifState === 'ask') {
      document.addEventListener('mousedown', handler);
      document.addEventListener('touchstart', handler);
    }
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [notifState]);

  // Breadcrumb dinâmico
  // (Moved higher up)

  const homeRoute = sections[0]?.items[0]?.to || '/admin/dashboard';

  // Rotas de menu do usuário baseadas no dashboard atual
  const userMenuRoutes = useMemo(() => {
    const base = location.pathname.split('/')[1]; // admin, professor, equipe, etc.
    const perfilByBase: Record<string, string> = {
      admin: '/admin/perfil',
      master: '/master/perfil',
      municipal: '/municipal/perfil',
      estadual: '/estadual/perfil',
      professor: '/professor/perfil',
      equipe: '/equipe/perfil',
      responsavel: '/responsavel/perfil',
      aluno: '/aluno/perfil',
      rede: '/rede/perfil',
      suporte: '/master/perfil',
      comercial: '/master/perfil',
      implantacao: '/master/perfil',
      agenda: '/master/perfil',
      tarefas: '/master/perfil',
    };
    const configByBase: Record<string, string> = {
      admin: '/admin/config/seguranca',
      master: '/master/configuracoes',
      municipal: '/municipal/configuracoes',
      estadual: '/estadual/configuracoes',
      professor: '/professor/configuracoes',
      equipe: '/equipe/configuracoes',
      responsavel: '/responsavel/configuracoes',
      aluno: '/aluno/configuracoes',
      rede: '/rede/configuracoes',
      suporte: '/master/configuracoes',
      comercial: '/master/configuracoes',
      implantacao: '/master/configuracoes',
      agenda: '/master/configuracoes',
      tarefas: '/master/configuracoes',
    };

    const perfilRoute = getPerfilRouteForUser(user?.perfil, location.pathname);
    const settingsRoute = configByBase[base] || perfilRoute;
    return { perfilRoute, settingsRoute };
  }, [location.pathname, user?.perfil]);

  // --- Mobile Bottom Navigation ---
  const findRouteByKeyword = (keywords: string[], fallback: string): string => {
    for (const section of sections) {
      for (const item of section.items) {
        const normalizedLabel = normalizeNavLabel(item.label);
        if (keywords.some(kw => normalizedLabel.includes(kw))) {
          return item.to;
        }
        if (item.subItems) {
          for (const sub of item.subItems) {
            const normalizedSubLabel = normalizeNavLabel(sub.label);
            if (keywords.some(kw => normalizedSubLabel.includes(kw))) {
              return sub.to;
            }
          }
        }
      }
    }
    return fallback;
  };

  const base = location.pathname.split('/')[1] || 'admin';
  const chatRoute = findRouteByKeyword(
    ['chat', 'mensagem', 'mensagens'],
    ['master', 'admin', 'municipal', 'estadual', 'professor', 'equipe'].includes(base)
      ? `/${base}/chat`
      : homeRoute
  );
  const escolasRoute = findRouteByKeyword(
    ['escola', 'escolas'],
    ['master', 'admin', 'municipal', 'estadual'].includes(base)
      ? `/${base}/escolas`
      : homeRoute
  );
  const comunicadosRoute = findRouteByKeyword(
    ['comunicado', 'comunicados'],
    ['master', 'admin', 'municipal', 'estadual', 'professor', 'equipe', 'responsavel', 'aluno'].includes(base)
      ? `/${base}/comunicados`
      : homeRoute
  );

  const mobileNavItems = useMemo(() => {
    return [
      { label: 'HOME', to: homeRoute, icon: Home },
      { label: 'CHAT', to: chatRoute, icon: MessageSquare, show: chatRoute !== homeRoute },
      { label: 'ESCOLAS', to: escolasRoute, icon: School, show: escolasRoute !== homeRoute },
      { label: 'COMUNICADOS', to: comunicadosRoute, icon: Megaphone, show: comunicadosRoute !== homeRoute },
      { label: 'PERFIL', to: userMenuRoutes.perfilRoute, icon: User },
    ].filter(item => item.show !== false);
  }, [homeRoute, chatRoute, escolasRoute, comunicadosRoute, userMenuRoutes.perfilRoute]);

  const isItemActive = (to: string) => {
    if (to === homeRoute) {
      return location.pathname === homeRoute;
    }
    return location.pathname.startsWith(to);
  };

  const PERFIL_COLORS: Record<string, string> = {
    ADMIN: '#000000', PROFESSOR: '#2d3748', ESCOLA: '#7c3aed',
    RESPONSAVEL: '#d97706', ALUNO: '#2563eb',
    MUNICIPAL: '#0ea5e9', ESTADUAL: '#10b981', REDE: '#8b5cf6',
    PUBLICO: '#10b981',
  };

  const CARGO_LABELS: Record<string, string> = {
    DIRETOR: 'Diretor(a)', COORDENACAO: 'Coordenação', SECRETARIA: 'Secretaria',
  };

  // Nome limpo para remover prefixos
  const cleanedName = useMemo(() => {
    if (!user?.nome) return '';
    return user.nome.replace(/^(Prof\.|Profa\.|Profª|Profº)\s+/i, '');
  }, [user?.nome]);

  // Iniciais do nome para avatar
  const initials = useMemo(() => {
    if (!cleanedName) return '??';
    const parts = cleanedName.split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
  }, [cleanedName]);

  const displayRole = cargo ? CARGO_LABELS[cargo] || cargo : perfil || user?.perfil || '';

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const toggleSection = (title: string) => {
    setCollapsed((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
    window.location.href = '/login';
  };

  /** Encerra sessao impersonada (limpa sessionStorage e fecha aba) */
  const handleEndImpersonate = () => {
    sessionStorage.removeItem('ac_impersonate');
    sessionStorage.removeItem('ac_token');
    sessionStorage.removeItem('ac_escolaId');
    sessionStorage.removeItem('ac_nomeEscola');
    sessionStorage.removeItem('ac_perfil');
    sessionStorage.removeItem('ac_cargo');
    sessionStorage.removeItem('ac_impersonatedByName');
    window.close();
    // Fallback se o browser nao permitir fechar
    window.location.href = '/login';
  };

  const PERFIL_DISPLAY: Record<string, string> = {
    ADMIN: 'Administrador', PROFESSOR: 'Professor', ESCOLA: 'Escola',
    RESPONSAVEL: 'Responsavel', ALUNO: 'Aluno',
    MUNICIPAL: 'Gestor Municipal', ESTADUAL: 'Gestor Estadual', REDE: 'Gestor de Rede',
    PUBLICO: 'Visitante',
  };

  const isRecordingMode = typeof window !== 'undefined' && (
    window.self !== window.top ||
    window.name === 'ecad-simulator-iframe' ||
    document.body.classList.contains('ac-recording-active')
  );

  const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isTest = typeof window !== 'undefined' && window.location.hostname === 'test.alunoconectado.com.br';
  const envLabel = null;
  const envColor: string = '';

  const sidebarFg = resolveSidebarTextColor(brandColor || '#1B365D', brandGradient);

  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const saved = sessionStorage.getItem('ac_sidebar_scroll_pos');
    if (saved && sidebarScrollRef.current) {
      sidebarScrollRef.current.scrollTop = Number(saved);
      requestAnimationFrame(() => {
        if (sidebarScrollRef.current && saved) {
          sidebarScrollRef.current.scrollTop = Number(saved);
        }
      });
    }
  }, [location.pathname, sidebarExpanded]);

  const renderSidebarContent = (railWide: boolean) => (
    <div
      data-sidebar-brand
      className={`flex h-full max-h-screen min-h-0 flex-col gap-0 ${darkMode ? 'border-r border-border' : ''}`}
      style={
        {
          background: darkMode
            ? 'hsl(var(--card))'
            : brandGradient || brandColor,
          color: darkMode ? undefined : sidebarFg,
          ...(darkMode
            ? {}
            : {
                ['--sidebar-brand-fg' as string]: sidebarFg,
                ['--sidebar-brand-accent' as string]: brandColor,
              }),
        } as CSSProperties
      }
    >
      <div
        className={
          railWide
            ? 'flex items-center border-b border-white/10 px-4 lg:px-6 justify-between'
            : 'hidden md:flex flex-col items-center gap-2 border-b border-white/10 px-2 py-3'
        }
        style={{ minHeight: railWide ? (nomeEscola ? 56 : 52) : undefined, paddingTop: railWide ? 8 : undefined, paddingBottom: railWide ? 8 : undefined }}
      >
        {railWide ? (
          <>
            <Link to={homeRoute} className="flex items-center gap-2.5 font-semibold text-white" style={{ overflow: 'hidden', minWidth: 0 }}>
              <img src="/icons/logo-shield.svg" alt="Aluno Conectado" style={{ width: 36, height: 36, flexShrink: 0 }} />
              <div style={{ overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                {brandSubtitle && brandSubtitle !== headerTitle ? (
                  <>
                    {/* Linha Superior: Categoria / Papel (Menor, uppercase) */}
                    <div style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.58rem',
                      lineHeight: 1.2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      opacity: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <span>{formatKicker(headerTitle)}</span>
                      {envLabel && (
                        <span style={{
                          padding: '0px 4px', borderRadius: 3, fontSize: '0.40rem', fontWeight: 800,
                          background: envColor + '30', color: envColor === '#ef4444' ? '#fca5a5' : '#fdba74',
                          border: `1px solid ${envColor}50`,
                          letterSpacing: '0.5px', lineHeight: 1.4, textTransform: 'uppercase', opacity: 1,
                        }}>
                          {envLabel}
                        </span>
                      )}
                    </div>

                    {/* Linha Inferior: Nome da Entidade / Cidade / Escola (Maior, destaque) */}
                    <div style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.86rem',
                      lineHeight: 1.25,
                      fontWeight: 800,
                      color: '#ffffff',
                      marginTop: 1
                    }}>
                      {brandSubtitle}
                    </div>
                  </>
                ) : nomeEscola ? (
                  <>
                    {/* Linha Superior: Título do Painel (Menor) */}
                    <div style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.58rem',
                      lineHeight: 1.2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      opacity: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <span>{formatKicker(headerTitle)}</span>
                      {envLabel && (
                        <span style={{
                          padding: '0px 4px', borderRadius: 3, fontSize: '0.40rem', fontWeight: 800,
                          background: envColor + '30', color: envColor === '#ef4444' ? '#fca5a5' : '#fdba74',
                          border: `1px solid ${envColor}50`,
                          letterSpacing: '0.5px', lineHeight: 1.4, textTransform: 'uppercase', opacity: 1,
                        }}>
                          {envLabel}
                        </span>
                      )}
                    </div>

                    {/* Linha Inferior: Nome da Escola (Maior) */}
                    <div style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.86rem',
                      lineHeight: 1.25,
                      fontWeight: 800,
                      color: '#ffffff',
                      marginTop: 1
                    }}>
                      {nomeEscola}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Linha Única: Título Principal */}
                    <div style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.86rem',
                      lineHeight: 1.2,
                      fontWeight: 800,
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5
                    }}>
                      {headerTitle === 'Aluno Conectado' ? (
                        <>Aluno <span style={{ color: '#a78bfa' }}>Conectado</span></>
                      ) : headerTitle}
                      {envLabel && (
                        <span style={{
                          padding: '0px 4px', borderRadius: 3, fontSize: '0.40rem', fontWeight: 800,
                          background: envColor + '30', color: envColor === '#ef4444' ? '#fca5a5' : '#fdba74',
                          border: `1px solid ${envColor}50`,
                          letterSpacing: '0.5px', lineHeight: 1.4, textTransform: 'uppercase', opacity: 1,
                        }}>
                          {envLabel}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Link>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => toggleSidebarExpanded()}
                className="hidden md:inline-flex p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/90"
                title="Recolher menu"
              >
                <ChevronsLeft size={20} />
              </button>
              <button onClick={() => setMobileOpen(false)} className="md:hidden p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white"><X size={20} /></button>
            </div>
          </>
        ) : (
          <>
            <Link to={homeRoute} className="flex flex-col items-center font-semibold text-white" title={headerTitle}>
              <div className="relative" style={{ width: 36, height: 36 }}>
                <img src="/icons/logo-shield.svg" alt="AC" style={{ width: 36, height: 36 }} />
                {envLabel && (
                  <span style={{
                    position: 'absolute', top: -5, right: -5,
                    width: 14, height: 14, borderRadius: '50%',
                    background: envColor, border: '2px solid hsl(var(--card))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'notif-dot-pulse 2s ease-in-out infinite',
                    boxShadow: `0 0 6px ${envColor}`,
                  }} title={isDev ? 'Ambiente Local' : 'Ambiente de Teste'}>
                    <AlertTriangle size={7} style={{ color: 'white', strokeWidth: 3 }} />
                  </span>
                )}
              </div>
            </Link>
            <button
              type="button"
              onClick={() => toggleSidebarExpanded()}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/90"
              title="Expandir menu"
            >
              <ChevronsRight size={20} />
            </button>
          </>
        )}
      </div>
      <div
        ref={sidebarScrollRef}
        onScroll={(e) => {
          sessionStorage.setItem('ac_sidebar_scroll_pos', e.currentTarget.scrollTop.toString());
        }}
        className="flex-1 min-h-0 overflow-auto py-1 ac-sidebar-scroll"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {sections
          .filter((s) => !s.adminOnly || isAdmin)
          .map((section) => ({
            ...section,
            items: section.items
              .filter((item) => {
                const to = item.to;
                const path = to.toLowerCase();
                // Folders (to='#') são filtrados depois com base nos subItems
                if (path === '#') return true;
                if (path.startsWith('/equipe/') || path === '/equipe') {
                  return canAccessEquipePath(to, user);
                }
                if (path.includes('/equipe')) return hasPermission(user, 'equipe.view');
                if (path.includes('/turnos')) return hasPermission(user, 'turnos.view');
                if (path.includes('/materias')) return hasPermission(user, 'materias.view');
                if (path.includes('/turmas')) return hasPermission(user, 'turmas.view');
                if (path.includes('/professores')) return hasPermission(user, 'professores.view');
                if (path.includes('/alunos')) return hasPermission(user, 'alunos.view');
                if (path.includes('/responsaveis')) return hasPermission(user, 'responsaveis.view');
                if (path.includes('/chamada') || path.includes('/frequencia')) return hasPermission(user, 'chamadas.view');
                if (path.includes('/comunicados')) return hasPermission(user, 'comunicados.view');
                if (path.includes('/midias')) return hasPermission(user, 'midias.view');
                if (path.includes('/acesso') || path.includes('/portaria')) return hasPermission(user, 'acesso.view');
                if (path.includes('/dispositivos')) return hasPermission(user, 'dispositivos.view');
                if (path.includes('/gestao/')) return hasPermission(user, 'gestao.view');
                if (path.includes('/relatorios')) return hasPermission(user, 'relatorios.view');
                if (path.includes('/config')) return hasPermission(user, 'configuracoes.manage');
                return true;
              })
              // Filtrar subItems de pastas por permissão
              .map((item) => {
                if (!item.subItems || item.subItems.length === 0) return item;
                const filteredSubs = item.subItems.filter((sub) => {
                  const sp = sub.to.toLowerCase();
                  if (sp.includes('/equipe')) return hasPermission(user, 'equipe.view');
                  if (sp.includes('/turnos')) return hasPermission(user, 'turnos.view');
                  if (sp.includes('/materias')) return hasPermission(user, 'materias.view');
                  if (sp.includes('/turmas')) return hasPermission(user, 'turmas.view');
                  if (sp.includes('/professores')) return hasPermission(user, 'professores.view');
                  if (sp.includes('/alunos')) return hasPermission(user, 'alunos.view');
                  if (sp.includes('/responsaveis')) return hasPermission(user, 'responsaveis.view');
                  if (sp.includes('/chamada') || sp.includes('/frequencia')) return hasPermission(user, 'chamadas.view');
                  if (sp.includes('/comunicados')) return hasPermission(user, 'comunicados.view');
                  if (sp.includes('/midias')) return hasPermission(user, 'midias.view');
                  if (sp.includes('/acesso') || sp.includes('/portaria')) return hasPermission(user, 'acesso.view');
                  if (sp.includes('/dispositivos')) return hasPermission(user, 'dispositivos.view');
                  if (sp.includes('/gestao/')) return hasPermission(user, 'gestao.view');
                  if (sp.includes('/relatorios')) return hasPermission(user, 'relatorios.view');
                  if (sp.includes('/config')) return hasPermission(user, 'configuracoes.manage');
                  return true;
                });
                return { ...item, subItems: filteredSubs };
              })
              // Remover pastas cujos subItems ficaram todos vazios
              .filter((item) => !(item.subItems && item.subItems.length === 0)),
          }))
          .filter((section) => section.items.length > 0)
          .map((section, si) => (
            <div key={si}>
              {section.title && railWide && (
                section.alwaysExpanded ? (
                  <div
                    data-tour={sectionTourId(section.title)}
                    className="flex w-full items-center px-3 lg:px-4 py-1 text-[12px] font-bold uppercase tracking-wide text-white"
                    style={{ opacity: 0.6 }}
                  >
                    {section.title}
                  </div>
                ) : (
                  <button
                    type="button"
                    data-tour={sectionTourId(section.title)}
                    onClick={() => toggleSection(section.title!)}
                    className="flex w-full items-center justify-between px-3 lg:px-4 py-1 text-[12px] font-bold uppercase tracking-wide text-white transition-colors"
                    style={{ opacity: 0.6 }}
                  >
                    {section.title}
                    <ChevronDown
                      size={16}
                      style={{
                        transform: collapsed[section.title!] ? 'rotate(-90deg)' : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </button>
                )
              )}
              {section.title && !railWide && si > 0 && <div className="mx-2 my-1 h-px bg-white/10" aria-hidden />}
              {(!section.title || section.alwaysExpanded || !collapsed[section.title!] || !railWide) && (
                <nav className={`grid items-start font-medium gap-px mb-0.5 ${railWide ? 'px-1.5 lg:px-3' : 'px-1.5'}`}>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isDesativadosLocked = (item.label === 'Desativados' || item.label === 'Outros') && !isDesativadosUnlocked;
                    const displaySubItems = hasSubItems && !isDesativadosLocked;

                    const isActive = (item.to === '#' && displaySubItems)
                      ? item.subItems!.some(sub => location.pathname === sub.to || location.pathname.startsWith(sub.to + '/'))
                      : location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                    const isFolderOpen = displaySubItems && (openFolders[item.label] ?? isActive);
                    const isChamadaPage = item.to.endsWith('/chamada');
                    const isLocked = chamadaAtiva && !isChamadaPage && !isActive;
                    const badge = item.badgeKey ? badges[item.badgeKey] : undefined;
                    const isSetupLocked = badge?.type === 'locked';
                    const subtitle = getSidebarSubtitle(item.label);
                    return (
                      <div key={item.to + item.label} className="relative">
                        <Link
                          to={isLocked || isSetupLocked ? '#' : item.to}
                          data-tour={item.to !== '#' ? navTourId(item.to) : undefined}
                          onClick={(e) => {
                            if (hasSubItems) {
                              e.preventDefault();
                              if (isDesativadosLocked) {
                                setUnlockFolderTarget(item.label);
                              } else {
                                setOpenFolders(prev => ({ ...prev, [item.label]: !isFolderOpen }));
                              }
                              return;
                            }
                            if (isLocked) {
                              e.preventDefault();
                              import('sonner').then(m => m.toast.warning('Finalize ou cancele a chamada antes de navegar.'));
                            } else if (isSetupLocked) {
                              e.preventDefault();
                              import('sonner').then(m => m.toast.info(badge?.tooltip || 'Configure os pre-requisitos primeiro.'));
                            } else { setMobileOpen(false); }
                          }}
                          className={`flex items-center transition-all border ${navSize === 'md' ? 'text-[14px]' : 'text-[13px]'
                            } ${railWide
                              ? navSize === 'md' ? 'gap-2.5 rounded px-2.5 py-2' : 'gap-2 rounded px-2.5 py-1.5'
                              : 'relative justify-center rounded-lg p-3'
                            } ${isActive
                              ? 'text-white bg-indigo-600/30 border-indigo-500/40 font-semibold shadow-sm'
                              : isLocked || isSetupLocked
                                ? 'text-white/50 border-transparent cursor-not-allowed'
                                : 'text-white hover:text-white hover:bg-white/10 border-transparent'
                            }`}
                          style={isLocked || isSetupLocked ? { pointerEvents: 'auto', opacity: 0.6 } : isDesativadosLocked ? { opacity: 0.6 } : undefined}
                          title={subtitle ? `${item.label} — ${subtitle}` : item.label}
                        >
                          <span className="relative inline-flex items-center justify-center shrink-0">
                            <Icon size={railWide ? (navSize === 'md' ? 20 : 18) : 22} className="shrink-0" strokeWidth={2.0} />
                            {!railWide && (
                              <>
                                {badge && badge.type === 'next' && (
                                  <span className="absolute -right-0.5 -bottom-0.5 w-1.5 h-1.5 rounded-full bg-sky-400 shadow-sm" style={{ animation: 'pulse 2s infinite' }} />
                                )}
                                {badge && badge.type === 'warning' && (badge.count ?? 0) > 0 && (
                                  <span className="absolute -right-0.5 -top-0.5 min-w-[5px] h-1.5 rounded-full bg-amber-400" />
                                )}
                                {item.label === 'Chat Interno' && chatUnreadCount > 0 && (
                                  <span className="absolute -right-0.5 -top-0.5 w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                                )}
                                {badge && badge.type === 'locked' && <Lock className="absolute -right-1 -bottom-0.5" size={9} style={{ opacity: 0.65 }} />}
                                {isLocked && !badge && <span className="absolute -right-0.5 -top-0.5 text-[0.5rem] leading-none opacity-80">🔒</span>}
                              </>
                            )}
                          </span>
                          {railWide && (
                            <span className="min-w-0 flex-1 border-b border-white/5 py-0.5 leading-none">
                              <span className={`block truncate font-semibold leading-snug text-white ${navSize === 'md' ? 'text-[0.92rem]' : 'text-[0.86rem]'
                                }`}>
                                {item.label}
                              </span>
                            </span>
                          )}
                          {railWide && (
                            <>
                              {item.label === 'Chat Interno' && chatUnreadCount > 0 && (
                                <span style={{
                                  marginLeft: 'auto', background: 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)', color: 'white',
                                  boxShadow: '0 0 8px rgba(56, 189, 248, 0.4)',
                                  borderRadius: 10, minWidth: 18, height: 18, display: 'flex',
                                  alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.65rem', fontWeight: 800, flexShrink: 0, padding: '0 4px',
                                }}>
                                  {chatUnreadCount}
                                </span>
                              )}
                              {badge && badge.type === 'warning' && (badge.count ?? 0) > 0 && (
                                <span style={{
                                  marginLeft: 'auto', background: '#fbbf24', color: '#92400e',
                                  borderRadius: 10, minWidth: 18, height: 18, display: 'flex',
                                  alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.6rem', fontWeight: 800, flexShrink: 0, padding: '0 4px',
                                }}>
                                  {badge.count}
                                </span>
                              )}
                              {badge && badge.type === 'next' && (
                                <span style={{
                                  marginLeft: 'auto',
                                  background: 'rgba(99, 102, 241, 0.22)',
                                  border: '1px solid rgba(99, 102, 241, 0.45)',
                                  color: '#a5b4fc',
                                  borderRadius: 4,
                                  padding: '1.5px 5px',
                                  fontSize: '0.52rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  animation: 'pulse 2s infinite',
                                }}>
                                  Próximo
                                </span>
                              )}
                              {badge && badge.type === 'locked' && (
                                <Lock size={12} style={{ marginLeft: 'auto', opacity: 0.5, flexShrink: 0 }} />
                              )}
                              {isLocked && !badge && <Lock size={12} style={{ marginLeft: 'auto', opacity: 0.5, flexShrink: 0 }} />}
                              {displaySubItems && (
                                <ChevronDown size={16} style={{ marginLeft: 'auto', opacity: 0.6, transform: isFolderOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                              )}
                            </>
                          )}
                        </Link>
                        {displaySubItems && isFolderOpen && railWide && (
                          <div className="flex flex-col ml-6 mt-0.5 border-l border-white/10 pl-2 gap-px">
                            {item.subItems!.map(sub => {
                              const isSubActive = location.pathname === sub.to || location.pathname.startsWith(sub.to + '/');
                              const subBadge = sub.badgeKey ? badges[sub.badgeKey] : undefined;
                              return (
                                <Link
                                  key={sub.to}
                                  to={sub.to}
                                  data-tour={navTourId(sub.to)}
                                  className={`py-1 px-2 text-[0.82rem] rounded transition-colors flex items-center justify-between ${isSubActive ? 'bg-white/15 text-white font-semibold' : 'text-white hover:text-white hover:bg-white/10'}`}
                                >
                                  {sub.label}
                                  {subBadge && subBadge.type === 'warning' && (subBadge.count ?? 0) > 0 && (
                                    <span style={{ background: '#fbbf24', color: '#92400e', borderRadius: 10, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 800, padding: '0 4px' }}>
                                      {subBadge.count}
                                    </span>
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              )}
            </div>
          ))}
      </div>
      <div className={`border-t border-white/10 ac-sidebar-footer ${railWide ? 'p-2.5' : 'p-2'}`}>
        {railWide ? (
          <div className="flex items-center justify-between gap-2">
            {/* Botão Sair */}
            <button
              onClick={handleLogout}
              type="button"
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-white/80 hover:text-red-400 hover:bg-white/5 transition-all"
              title="Sair"
            >
              <LogOut size={16} />
              <span>Sair</span>
            </button>

            {/* Ações Rápidas no final da linha (Cadeado e Paleta de Cores) */}
            <div className="flex items-center gap-1.5">
              {/* Botão Bloquear Tela (Cadeado) */}
              <button
                onClick={() => window.dispatchEvent(new Event('ac_force_lock'))}
                title="Bloquear Tela"
                className="hover:bg-white/10 transition-colors"
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.7)',
                  padding: 0,
                }}
              >
                <Lock size={15} />
              </button>

              {/* Botão Personalizar Tema (Paleta de Cores) */}
              <button
                onClick={() => setShowThemeModal(true)}
                title="Personalizar Cores e Layout"
                className="hover:bg-white/10 transition-colors"
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.7)',
                  padding: 0,
                }}
              >
                <Palette size={15} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => window.dispatchEvent(new Event('ac_force_lock'))}
              title="Bloquear Tela"
              className="hover:bg-white/10 transition-colors"
              style={{
                background: 'none',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8,
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                padding: 0,
              }}
            >
              <Lock size={15} />
            </button>

            <button
              onClick={() => setShowThemeModal(true)}
              title="Personalizar Cores e Layout"
              className="hover:bg-white/10 transition-colors"
              style={{
                background: 'none',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8,
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                padding: 0,
              }}
            >
              <Palette size={15} />
            </button>

            <button
              onClick={handleLogout}
              type="button"
              className="flex w-full items-center justify-center rounded-lg p-2 text-white/80 hover:text-red-400 hover:bg-white/5 transition-all"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const perfilColor = PERFIL_COLORS[user?.perfil || ''] || '#6b7280';
  const impersonatedByName = sessionStorage.getItem('ac_impersonatedByName') || '';

  const isChatDockedAndOpen = chatOpen && isDocked;
  const gridStyle = useMemo(() => {
    if (setupGuard) {
      return {
        gridTemplateColumns: `240px 1fr${isChatDockedAndOpen ? ' 480px' : ''}`
      };
    }
    if (sidebarExpanded) {
      return {
        gridTemplateColumns: `minmax(200px, ${sidebarWidth}px) 1fr${isChatDockedAndOpen ? ' 480px' : ''}`
      };
    } else {
      return {
        gridTemplateColumns: `72px 1fr${isChatDockedAndOpen ? ' 480px' : ''}`
      };
    }
  }, [sidebarExpanded, sidebarWidth, isChatDockedAndOpen, setupGuard]);

  const layoutContent = (
    <div
      className={
        useShadcnSidebar
          ? "flex h-screen w-screen overflow-hidden bg-background text-foreground"
          : setupGuard
            ? 'h-screen w-full overflow-hidden md:grid md:grid-cols-[240px_1fr]'
            : sidebarExpanded
              ? 'h-screen w-full overflow-hidden md:grid'
              : `h-screen w-full overflow-hidden md:grid ${!isChatDockedAndOpen ? 'md:grid-cols-[72px_1fr]' : ''}`
      }
      style={
        useShadcnSidebar
          ? {}
          : {
              ...gridStyle,
              background: 'linear-gradient(160deg, #19072d 0%, #3b1682 52%, #8b5cf6 100%)',
            }
      }
    >
      <PwaPushEnforcer />
      {setupGuard ? (
        <aside
          className="ac-layout-sidebar hidden min-h-0 min-w-0 md:flex md:flex-col h-screen sticky top-0 transition-[width] duration-200"
          style={{
            width: 240,
            background: 'linear-gradient(180deg, #1e3a8a 0%, #2563eb 42%, #3b82f6 100%)',
            borderRight: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <div className="flex flex-col h-full text-white select-none">
            {/* Brand Area */}
            <div className="p-5 border-b border-white/5 flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-extrabold text-sm shadow-[0_0_12px_rgba(59,130,246,0.4)] shrink-0">
                  AC
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-bold text-white truncate leading-tight">{nomeEscola}</span>
                  <span className="text-xs text-white/70 truncate leading-none mt-1 font-medium">
                    {user?.nome || 'Diretor(a)'}
                  </span>
                </div>
              </div>
            </div>

            {/* Steps List */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
              <span className="text-[10px] font-bold text-white/80 uppercase tracking-widest px-2 mb-2">Trilha de Implantação</span>
              {setupGuard.etapas.map((etapa, idx) => {
                const isCompleted = etapa.status === 'concluida';
                const isPartial = etapa.status === 'parcial';
                const isLocked = etapa.status === 'bloqueada';
                const isActive = location.pathname === etapa.rota;

                // Resolve Lucide Icon
                let IconComponent: any = Lock;
                if (!isLocked) {
                  for (const s of sections) {
                    for (const item of s.items) {
                      if (item.to === etapa.rota) {
                        IconComponent = item.icon;
                        break;
                      }
                    }
                  }
                }
                if (etapa.id === 'grade') IconComponent = BookOpen;

                // Click Handler
                const handleStepClick = (e: React.MouseEvent) => {
                  if (isLocked) {
                    e.preventDefault();
                    toast.warning(`Etapa bloqueada. Conclua as ações anteriores primeiro!`);
                  } else {
                    navigate(etapa.rota);
                  }
                };

                return (
                  <div
                    key={etapa.id}
                    onClick={handleStepClick}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all duration-150 cursor-pointer ${
                      isActive
                        ? 'bg-white text-blue-950 shadow-[0_4px_16px_rgba(0,0,0,0.25)]'
                        : isLocked
                        ? 'opacity-30 cursor-not-allowed hover:bg-transparent text-white'
                        : 'hover:bg-white/10 hover:text-white text-white'
                    }`}
                  >
                    {/* Step indicator */}
                    <div className="shrink-0 flex items-center justify-center">
                      {isCompleted ? (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                          isActive
                            ? 'bg-emerald-500 text-white border-emerald-600'
                            : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                        }`}>
                          <CheckCircle2 size={11} strokeWidth={3} />
                        </div>
                      ) : isPartial ? (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                          isActive
                            ? 'bg-amber-500 text-white border-amber-600'
                            : 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                        }`}>
                          <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        </div>
                      ) : isActive ? (
                        <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center border border-blue-700 animate-pulse">
                          <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        </div>
                      ) : isLocked ? (
                        <div className="w-5 h-5 rounded-full bg-white/10 text-white/40 flex items-center justify-center border border-white/5">
                          <Lock size={10} />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-white/10 text-white/70 flex items-center justify-center border border-white/10 text-[10px]">
                          {idx + 1}
                        </div>
                      )}
                    </div>

                    {/* Step info */}
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      {IconComponent && (
                        <IconComponent
                          size={14}
                          className={`shrink-0 ${
                            isActive ? 'text-blue-700 opacity-100' : 'opacity-80'
                          }`}
                        />
                      )}
                      <span className={`truncate leading-none ${isActive ? 'font-bold text-blue-950' : 'text-white/90'}`}>{etapa.nome}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer Area */}
            <div className="p-4 border-t border-white/5 flex flex-col gap-2">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className={`w-full py-2 px-3 rounded-lg text-center text-xs font-bold transition-all duration-150 ${
                  location.pathname === '/admin/dashboard'
                    ? 'bg-slate-900/60 text-slate-500 cursor-default border border-white/5'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-500 hover:to-blue-600 shadow-md shadow-blue-900/40 active:translate-y-px'
                }`}
                disabled={location.pathname === '/admin/dashboard'}
              >
                Painel do Setup
              </button>
            </div>
          </div>
        </aside>
      ) : useShadcnSidebar ? (
        <Sidebar collapsible="icon" className="border-r border-sidebar-border shrink-0">
          <ShadcnSidebarContent
            sections={sections}
            logout={logout}
            nomeEscola={nomeEscola}
            headerTitle={headerTitle}
            brandSubtitle={brandSubtitle}
            brandColor={brandColor}
            user={user}
          />
        </Sidebar>
      ) : (
        <>
          <aside className="ac-layout-sidebar hidden min-h-0 min-w-0 md:flex md:flex-col h-screen sticky top-0 transition-[width] duration-200 ac-animate-sidebar" style={{ boxShadow: '2px 0 10px rgba(0,0,0,0.1)' }}>{renderSidebarContent(sidebarExpanded)}</aside>
          {mobileOpen && (
            <>
              <div className="fixed inset-0 bg-black/50 z-40 md:hidden animate-fadeIn" onClick={() => setMobileOpen(false)} />
              <aside className="ac-layout-sidebar fixed inset-y-0 left-0 w-[280px] z-50 md:hidden animate-slideInLeft shadow-xl min-h-0">{renderSidebarContent(true)}</aside>
            </>
          )}
        </>
      )}
      {(() => {
        const renderRightPane = (outletNode: React.ReactNode, isPrev: boolean) => {
          return (
            <div style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: (useShadcnSidebar && isMobileDevice) ? 'visible' : 'hidden',
              width: '100%',
              height: (useShadcnSidebar && isMobileDevice) ? 'auto' : '100%'
            }}>
              {/* ═══ INDICADOR AMBIENTE (movido para sidebar badge) ═══ */}
        {/* ═══ BANNER ACESSO ADMINISTRATIVO ═══ */}
        {isImpersonating && (typeof window === 'undefined' || window.parent === window) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
            background: 'linear-gradient(90deg, #fef3c7, #fde68a)',
            borderBottom: '2px solid #f59e0b',
            fontSize: '.78rem', fontWeight: 600, color: '#92400e',
            zIndex: 9999, flexShrink: 0,
          }}>
            <Shield size={16} style={{ color: '#d97706', flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              Acesso Administrativo — Visualizando como <strong>{cleanedName || 'usuario'}</strong>
              <span style={{
                padding: '1px 6px', borderRadius: 4, fontSize: '.62rem', fontWeight: 700,
                background: '#f59e0b20', color: '#b45309', marginLeft: 6,
              }}>{PERFIL_DISPLAY[user?.perfil || ''] || user?.perfil}</span>
              {impersonatedByName && (
                <span style={{ fontSize: '.7rem', color: '#a16207', marginLeft: 8 }}>
                  (autorizado por {impersonatedByName})
                </span>
              )}
            </span>
            <button onClick={handleEndImpersonate} style={{
              padding: '4px 12px', borderRadius: 6, fontSize: '.72rem', fontWeight: 700,
              background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <X size={12} /> Encerrar
            </button>
          </div>
        )}
        <header
          className={`${location.pathname.startsWith('/professor') || location.pathname.startsWith('/responsavel') ? 'hidden md:flex' : 'flex'
            } gap-4 border-b px-4 lg:px-6 justify-between shrink-0 items-center ${location.pathname === '/admin/dashboard' ? 'min-h-14 py-2 lg:min-h-[60px]' : 'h-14 lg:h-[60px]'
            }${!envLabel ? ' bg-card' : ' ac-env-header'}${!(location.pathname.startsWith('/professor') || location.pathname.startsWith('/responsavel'))
              ? ' ac-mobile-dark-header'
              : ''
            }`}
          style={{
            position: 'relative' as const,
            zIndex: 50,
            ...(envLabel ? {
              background: isDev
                ? 'linear-gradient(90deg, hsl(0 70% 18%), hsl(0 60% 14%))'
                : 'linear-gradient(90deg, hsl(25 70% 18%), hsl(25 60% 14%))',
              borderBottom: 'none',
              color: isDev ? '#fca5a5' : '#fdba74',
            } : {}),
            ...(isRecordingActive && showRedHeader ? {
              background: 'linear-gradient(90deg, #dc2626, #b91c1c)',
              color: '#ffffff',
              borderBottom: '1px solid #ef4444',
            } : {})
          }}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {setupGuard ? (
              location.pathname !== '/admin/dashboard' ? (
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors text-slate-700 text-xs font-bold border border-slate-200 shrink-0"
                >
                  <ChevronsLeft size={14} />
                  <span>Voltar ao Setup</span>
                </button>
              ) : null
            ) : useShadcnSidebar ? (
              <SidebarTrigger className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0 text-foreground" />
            ) : (
              <button onClick={() => setMobileOpen(true)} className="md:hidden p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"><Menu size={22} /></button>
            )}
            <div className="flex flex-col justify-center gap-0.5 min-w-0 py-0.5">
              <div className="flex items-center gap-1.5 text-sm flex-wrap" style={{ overflow: 'hidden' }}>
                <span className="font-semibold flex items-center gap-2" style={{
                  fontSize: '1rem',
                  whiteSpace: 'nowrap',
                  color: isRecordingActive && showRedHeader
                    ? '#ffffff'
                    : envLabel ? (isDev ? '#fecaca' : '#fed7aa') : 'hsl(var(--foreground))'
                }}>
                  {location.pathname === '/admin/dashboard' && setupGuard ? (
                    <>
                      <span>✨</span>
                      <span>Bem-vindo ao Painel Escola</span>
                    </>
                  ) : (
                    <>
                      {currentPage.icon && (
                        <currentPage.icon
                          size={18}
                          className="text-muted-foreground shrink-0"
                          style={{ color: envLabel ? 'inherit' : 'hsl(var(--muted-foreground))' }}
                          aria-hidden
                        />
                      )}
                      <span>{currentPage.label}</span>
                    </>
                  )}
                </span>
                {envLabel && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    padding: '2px 8px', borderRadius: 5, fontSize: '0.58rem', fontWeight: 800,
                    background: '#111',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    letterSpacing: '0.8px', lineHeight: 1.2, flexShrink: 0,
                    textTransform: 'uppercase',
                  }}>
                    {envLabel}
                  </span>
                )}
                {typeof window !== 'undefined' && localStorage.getItem('ac_demo_mode') === 'true' && !window.frameElement && window.innerWidth > 500 && (
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: '0.6rem', fontWeight: 800,
                    background: 'linear-gradient(135deg, #f59e0b, #ef4444)', color: 'white',
                    textTransform: 'uppercase', letterSpacing: '1px', marginLeft: 6,
                    animation: 'pulse 2s infinite', boxShadow: '0 0 8px rgba(245,158,11,0.4)',
                  }}>
                    MODO DEMO
                  </span>
                )}
              </div>
              {location.pathname === '/admin/dashboard' && setupGuard ? (
                <p className="text-[11px] leading-snug text-muted-foreground max-w-3xl truncate">
                  Finalize a configuração inicial de <strong>{nomeEscola}</strong> para começar a usar chamada, comunicados, portaria e acompanhamento dos alunos.
                </p>
              ) : (
                location.pathname === '/admin/dashboard' && nomeEscola && (
                  <p className="text-[11px] leading-snug text-muted-foreground max-w-3xl truncate">
                    {nomeEscola}
                  </p>
                )
              )}
              {(location.pathname === '/admin/acesso' ||
                location.pathname.startsWith('/admin/acesso/') ||
                location.pathname === '/admin/portaria') && (
                  <p
                    className="ac-header-subtitle-preview"
                    title="Modo demonstração: indicadores, mapa tático e gráficos simulados para apresentação à prefeitura. RFID, leitores e tempo real na próxima fase."
                  >
                    <span className="ac-header-subtitle-preview__pill">Preview</span>
                    <span className="ac-header-subtitle-preview__text">
                      <strong>Demo</strong> — dados simulados · integração RFID em desenvolvimento
                    </span>
                  </p>
                )}
            </div>
          </div>



          {/* ═══ Header Right: User Menu ═══ */}
          {setupGuard ? (
            <div className="flex items-center gap-4">
              {location.pathname !== '/admin/dashboard' && (
                <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded border border-indigo-100 hidden md:inline-block">
                  Modo Setup Ativo
                </span>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-slate-500 hover:text-red-500 hover:bg-red-50 transition-colors text-xs font-bold border border-transparent hover:border-slate-200"
                title="Sair"
              >
                <LogOut size={14} />
                <span>Sair</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">

            {/* ═══ Ajuda Contextual Header Button ═══ */}
            {showHelpIcon && (
              <HelpButton
                onOpenHelp={help.openHelp}
                className={envLabel ? 'hover:opacity-80' : 'text-foreground hover:bg-muted'}
                style={{
                  background: envLabel ? 'rgba(255,255,255,0.95)' : 'none',
                  border: `1px solid ${envLabel ? 'rgba(255,255,255,0.3)' : 'hsl(var(--border))'}`,
                  borderRadius: 8,
                  width: 34,
                  height: 34,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  position: 'relative',
                  color: envLabel ? '#1e1e1e' : undefined,
                }}
              />
            )}

            {/* ═══ Chat Interno Header Button ═══ */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (location.pathname.endsWith('/chat')) return;

                  // 1. Se a janela externa estiver aberta e ativa, foca ela e dispara o efeito de alerta/flash
                  const lastActive = Number(localStorage.getItem('ac_chat_popout_last_active') || 0);
                  const isPopoutActive = Date.now() - lastActive < 6000;

                  if (isPopoutActive) {
                    try {
                      const channel = new BroadcastChannel('ac_chat_popout_sync');
                      channel.postMessage({ type: 'FOCUS_AND_FLASH' });
                      channel.close();

                      const existingWin = window.open('', 'ac_chat_external_window');
                      if (existingWin && !existingWin.closed && existingWin.location.href !== 'about:blank') {
                        existingWin.focus();
                      }
                    } catch {
                      // Fallback
                    }
                    return;
                  }

                  setChatOpen((prev) => !prev);
                }}
                title="Chat Interno"
                style={{
                  background: envLabel ? 'rgba(255,255,255,0.95)' : 'none',
                  border: `1px solid ${envLabel ? 'rgba(255,255,255,0.3)' : 'hsl(var(--border))'}`, borderRadius: 8,
                  width: 34, height: 34, cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
                  position: 'relative', color: envLabel ? '#1e1e1e' : undefined,
                }}
                className={envLabel ? 'hover:opacity-80' : 'text-foreground hover:bg-muted'}
              >
                <MessageSquare size={16} />
                {chatUnreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)',
                    boxShadow: '0 0 8px rgba(56, 189, 248, 0.6)',
                    color: 'white',
                    borderRadius: '50%',
                    minWidth: 14,
                    height: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.55rem',
                    fontWeight: 800,
                    padding: '0 2px',
                    zIndex: 10,
                  }}>
                    {chatUnreadCount}
                  </span>
                )}
              </button>
            </div>

            {/* ═══ Notification Bell ═══ */}
            {(() => {
              const isEmailUnverified = !!user && !user.emailVerificado;
              const totalSecurityAlerts = (isEmailUnverified ? 1 : 0) + (isPushNotActive ? 1 : 0) + unreadApprovals.length + inactiveDevices.length;
              const totalPendingAlerts = totalSecurityAlerts + unreadComunicados.length;
              const hasCriticalAlert = isEmailUnverified || unreadApprovals.length > 0 || inactiveDevices.length > 0;
              const hasOnlyPushSecurityAlert = !hasCriticalAlert && isPushNotActive;

              const badgeBackground = hasCriticalAlert
                ? '#ef4444'
                : hasOnlyPushSecurityAlert
                ? '#d97706'
                : '#2563eb';

              return (
                <div style={{ position: 'relative' }}>
                  <button
                    ref={notifButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof window !== 'undefined' && 'Notification' in window && typeof (window as any).Notification !== 'undefined') {
                        setPermissionStatus((window as any).Notification.permission);
                      }
                      if (notifState === 'ask') {
                        setNotifState('idle');
                      } else {
                        // Se não tem comunicados não lidos e há pendências de segurança, abre direto na aba de Segurança
                        if (unreadComunicados.length === 0 && totalSecurityAlerts > 0) {
                          setNotifTab('seguranca');
                        } else {
                          setNotifTab('comunicados');
                        }
                        setNotifState('ask');
                        setHasOpenedList(true);
                        fetchPendingApprovals();
                        fetchRecentComunicados();
                      }
                    }}
                    title="Notificações"
                    style={{
                      background: envLabel ? 'rgba(255,255,255,0.95)' : 'none',
                      border: `1px solid ${envLabel ? 'rgba(255,255,255,0.3)' : 'hsl(var(--border))'}`, borderRadius: 8,
                      width: 34, height: 34, cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
                      position: 'relative', color: envLabel ? '#1e1e1e' : undefined,
                    }}
                    className={`${envLabel ? 'hover:opacity-80' : 'text-foreground hover:bg-muted'} ${hasExpiredUnread ? 'ac-bell-pulsing-red' : (isPushNotActive ? 'ac-bell-pulsing-yellow' : '')}`}
                  >
                    <Bell size={16} />
                    {totalPendingAlerts > 0 && (
                      <span style={{
                        position: 'absolute', top: -5, right: -5,
                        background: badgeBackground,
                        color: '#ffffff',
                        borderRadius: '999px',
                        minWidth: 16, height: 16,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '.62rem', fontWeight: 800,
                        padding: '0 4px',
                        boxShadow: '0 0 0 2px hsl(var(--card))',
                        zIndex: 10,
                        animation: hasCriticalAlert ? 'none' : 'notif-dot-pulse 2s infinite',
                      }}>
                        {totalPendingAlerts > 99 ? '99+' : totalPendingAlerts}
                      </span>
                    )}
                  </button>

                  {/* Notification Popup via Portal */}
                  {notifState === 'ask' && typeof document !== 'undefined' && createPortal(
                    <>
                      {/* Mobile Backdrop */}
                      <div
                        className="fixed inset-0 bg-black/40 z-[99998] md:hidden animate-fadeIn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNotifState('idle');
                        }}
                      />
                      <div
                        ref={notifMenuRef}
                        className="notif-dropdown"
                        style={(() => {
                          const isMob = typeof window !== 'undefined' && window.innerWidth < 768;
                          if (isMob) {
                            return {
                              position: 'fixed' as const,
                              top: 56,
                              bottom: 72,
                              left: 12,
                              right: 12,
                              width: 'auto',
                              maxHeight: 'calc(100dvh - 136px)',
                              zIndex: 99999,
                            };
                          }
                          const rect = notifButtonRef.current?.getBoundingClientRect();
                          return {
                            position: 'fixed' as const,
                            top: rect ? rect.bottom + 8 : 60,
                            right: rect ? Math.max(12, window.innerWidth - rect.right) : 16,
                            width: 360,
                            zIndex: 99999,
                          };
                        })()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <span style={{ fontSize: '.85rem', fontWeight: 800, color: 'hsl(var(--foreground))', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Bell size={14} /> Notificações & Avisos
                            </span>
                            {totalPendingAlerts > 0 && (
                              <span style={{
                                background: hasCriticalAlert ? '#ef4444' : '#2563eb', color: '#fff', fontSize: '.68rem', fontWeight: 800,
                                padding: '2px 8px', borderRadius: 10,
                              }}>
                                {totalPendingAlerts} {totalPendingAlerts === 1 ? 'não lido' : 'não lidos'}
                              </span>
                            )}
                          </div>

                          {/* Abas Dual: Comunicados vs Segurança */}
                          <div style={{ display: 'flex', gap: 4, marginBottom: 12, background: 'hsl(var(--muted))', padding: 3, borderRadius: 8 }}>
                            <button
                              type="button"
                              onClick={() => setNotifTab('comunicados')}
                              style={{
                                flex: 1,
                                padding: '6px 8px',
                                borderRadius: 6,
                                border: 'none',
                                fontSize: '.72rem',
                                fontWeight: notifTab === 'comunicados' ? 700 : 500,
                                background: notifTab === 'comunicados' ? 'hsl(var(--card))' : 'transparent',
                                color: notifTab === 'comunicados' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                                boxShadow: notifTab === 'comunicados' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 6,
                                transition: 'all 0.15s',
                              }}
                            >
                              <Megaphone size={12} />
                              <span>Comunicados</span>
                              {unreadComunicados.length > 0 && (
                                <span style={{ background: '#2563eb', color: '#fff', fontSize: '.62rem', padding: '1px 5px', borderRadius: 10, fontWeight: 700 }}>
                                  {unreadComunicados.length}
                                </span>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={() => setNotifTab('seguranca')}
                              style={{
                                flex: 1,
                                padding: '6px 8px',
                                borderRadius: 6,
                                border: 'none',
                                fontSize: '.72rem',
                                fontWeight: notifTab === 'seguranca' ? 700 : 500,
                                background: notifTab === 'seguranca' ? 'hsl(var(--card))' : 'transparent',
                                color: notifTab === 'seguranca' ? (hasCriticalAlert ? '#dc2626' : hasOnlyPushSecurityAlert ? '#d97706' : 'hsl(var(--foreground))') : 'hsl(var(--muted-foreground))',
                                boxShadow: notifTab === 'seguranca' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 6,
                                transition: 'all 0.15s',
                              }}
                            >
                              <Shield size={12} />
                              <span>Segurança</span>
                              {totalSecurityAlerts > 0 && (
                                <span style={{ background: hasCriticalAlert ? '#ef4444' : hasOnlyPushSecurityAlert ? '#d97706' : '#f59e0b', color: '#fff', fontSize: '.62rem', padding: '1px 5px', borderRadius: 10, fontWeight: 700 }}>
                                  {totalSecurityAlerts}
                                </span>
                              )}
                            </button>
                          </div>

                          {/* ─── ABA 1: COMUNICADOS ESCOLARES / MUNICIPAIS ─── */}
                          {notifTab === 'comunicados' && (
                            <div>
                              {unreadComunicados.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '28px 12px', color: 'hsl(var(--muted-foreground))' }}>
                                  <div style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    background: '#ecfdf5',
                                    color: '#10b981',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 10px',
                                  }}>
                                    <CheckCircle2 size={22} />
                                  </div>
                                  <strong style={{ fontSize: '.84rem', color: 'hsl(var(--foreground))', display: 'block' }}>
                                    Tudo em dia!
                                  </strong>
                                  <p style={{ fontSize: '.74rem', margin: '4px 0 12px', color: 'hsl(var(--muted-foreground))' }}>
                                    Você não possui comunicados pendentes de leitura.
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNotifState('idle');
                                      const targetRoute =
                                        user?.perfil === 'MUNICIPAL'
                                          ? '/municipal/alertas'
                                          : user?.perfil === 'PROFESSOR'
                                            ? '/professor/alertas'
                                            : user?.perfil === 'RESPONSAVEL'
                                              ? '/responsavel/comunicados'
                                              : user?.perfil === 'ALUNO'
                                                ? '/aluno/alertas'
                                                : user?.perfil === 'ADMIN'
                                                  ? '/master/alertas'
                                                  : user?.perfil === 'ESTADUAL'
                                                    ? '/estadual/alertas'
                                                    : user?.perfil === 'REDE'
                                                      ? '/rede/alertas'
                                                      : '/admin/alertas';
                                      navigate(targetRoute);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#2563eb',
                                      fontSize: '.74rem',
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    Ver histórico completo
                                  </button>
                                </div>
                              ) : (
                                <div className="notif-list-container" style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  {unreadComunicados.map((c) => {
                                    const midiaUrl =
                                      c.configPreview?.midiaUrl ||
                                      c.configPreview?.arquivoUrl ||
                                      c.midiaUrl ||
                                      c.imagemUrl ||
                                      c.anexoUrl ||
                                      c.arquivoUrl;

                                    const senderName =
                                      c.municipio?.nome ? `Município de ${c.municipio.nome}` :
                                      c.escola?.nome ? c.escola.nome :
                                      c.criadoPor?.nome ? c.criadoPor.nome : 'Administração';

                                    const destName = (c.destinatario || c.publicoAlvo || 'TODOS').toUpperCase();
                                    const destLabel =
                                      destName.includes('PROFESSOR') ? 'Professores' :
                                      destName.includes('RESPONSAVEL') || destName.includes('PAIS') ? 'Responsáveis' :
                                      destName.includes('ALUNO') ? 'Alunos' :
                                      destName.includes('EQUIPE') ? 'Equipe' : 'Geral';

                                    const dateObj = new Date(c.criadoEm || Date.now());
                                    const diffMin = Math.floor((Date.now() - dateObj.getTime()) / 60000);
                                    const relTime =
                                      diffMin < 1 ? 'agora' :
                                      diffMin < 60 ? `há ${diffMin}m` :
                                      diffMin < 1440 ? `há ${Math.floor(diffMin / 60)}h` :
                                      `${Math.floor(diffMin / 1440)}d atrás`;

                                    return (
                                      <div
                                        key={c.id}
                                        onClick={() => {
                                          markComunicadoAsRead(c.id);
                                          setNotifState('idle');
                                          setVisualizandoComunicado(c);
                                        }}
                                        style={{
                                          padding: '10px 12px',
                                          borderRadius: 10,
                                          background: 'rgba(37, 99, 235, 0.06)',
                                          border: '1px solid rgba(37, 99, 235, 0.25)',
                                          fontSize: '.75rem',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 6,
                                          textAlign: 'left',
                                          cursor: 'pointer',
                                          transition: 'all 0.15s',
                                        }}
                                      >
                                        {/* Header do item no popover */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                                            <span style={{
                                              fontSize: '.62rem',
                                              fontWeight: 700,
                                              padding: '1px 6px',
                                              borderRadius: 4,
                                              background: '#e0f2fe',
                                              color: '#0369a1',
                                              whiteSpace: 'nowrap',
                                            }}>
                                              {senderName.length > 20 ? senderName.substring(0, 20) + '…' : senderName}
                                            </span>
                                            <span style={{
                                              fontSize: '.58rem',
                                              fontWeight: 600,
                                              padding: '1px 5px',
                                              borderRadius: 4,
                                              background: 'hsl(var(--secondary))',
                                              color: 'hsl(var(--muted-foreground))',
                                              whiteSpace: 'nowrap',
                                            }}>
                                              {destLabel}
                                            </span>
                                          </div>

                                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                            <span style={{ background: '#2563eb', color: '#fff', fontSize: '.55rem', fontWeight: 800, padding: '1px 5px', borderRadius: 6 }}>
                                              NOVO
                                            </span>
                                            <span style={{
                                              fontWeight: 700,
                                              fontSize: '.6rem',
                                              padding: '1px 5px',
                                              borderRadius: 4,
                                              background: c.tipo === 'URGENTE' ? '#fee2e2' : 'hsl(var(--secondary))',
                                              color: c.tipo === 'URGENTE' ? '#dc2626' : '#475569',
                                            }}>
                                              {c.tipo || 'AVISO'}
                                            </span>
                                          </div>
                                        </div>

                                        {/* Corpo com miniatura ao lado */}
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 800, color: 'hsl(var(--foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {c.titulo}
                                            </div>
                                            {c.corpo && (
                                              <p style={{ margin: '2px 0 0', fontSize: '.68rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                {c.corpo}
                                              </p>
                                            )}
                                          </div>

                                          {midiaUrl && (
                                            <img
                                              src={midiaUrl}
                                              alt=""
                                              style={{
                                                width: 42,
                                                height: 42,
                                                borderRadius: 6,
                                                objectFit: 'cover',
                                                border: '1px solid hsl(var(--border))',
                                                flexShrink: 0,
                                              }}
                                            />
                                          )}
                                        </div>

                                        {/* Rodapé: tempo decorrido e data */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '.62rem', color: 'hsl(var(--muted-foreground))', borderTop: '1px solid hsl(var(--border))', paddingTop: 4 }}>
                                          <span style={{ fontWeight: 700, color: '#3b82f6' }}>
                                            ⏱️ {relTime}
                                          </span>
                                          <span>
                                            📅 {dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às {dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          {/* ─── ABA 2: SEGURANÇA E PENDÊNCIAS DA CONTA ─── */}
                          {notifTab === 'seguranca' && (
                            <div>
                              {user && !user.emailVerificado && (
                                <div
                                  onClick={handleTriggerEmailVerification}
                                  style={{
                                    background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(220,38,38,0.12) 100%)',
                                    border: '1px solid rgba(239,68,68,0.3)',
                                    borderRadius: 12,
                                    padding: '12px 14px',
                                    marginBottom: 12,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                    textAlign: 'left',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.borderColor = '#ef4444'}
                                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: '.76rem', color: '#ef4444' }}>
                                    <AlertTriangle size={14} />
                                    E-mail não verificado
                                  </div>
                                  <p style={{ margin: 0, fontSize: '.68rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.4 }}>
                                    Sua conta está vulnerável. Clique aqui para confirmar seu e-mail agora.
                                  </p>
                                </div>
                              )}

                              {isPushNotActive && (
                                <div style={{
                                  background: permissionStatus === 'denied'
                                    ? 'linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(220,38,38,0.08) 100%)'
                                    : 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(217,119,6,0.08) 100%)',
                                  border: permissionStatus === 'denied'
                                    ? '1px solid rgba(239,68,68,0.2)'
                                    : '1px solid rgba(245,158,11,0.2)',
                                  borderRadius: 12,
                                  padding: 12,
                                  marginBottom: 12,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 6,
                                  textAlign: 'left',
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontWeight: 700,
                                    fontSize: '.75rem',
                                    color: permissionStatus === 'denied' ? '#ef4444' : '#d97706'
                                  }}>
                                    <AlertTriangle size={13} />
                                    {permissionStatus === 'denied' ? 'Notificações Bloqueadas' : 'Ativar Notificações'}
                                  </div>
                                  <p style={{ margin: 0, fontSize: '.68rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.4 }}>
                                    {permissionStatus === 'denied'
                                      ? 'As notificações foram bloqueadas nas configurações do seu navegador. Clique no ícone de cadeado ao lado da URL e altere a permissão para Permitir.'
                                      : 'Ative as notificações push para receber alertas de logins e acessos importantes nesta máquina em tempo real.'}
                                  </p>
                                  {permissionStatus !== 'denied' && (
                                    <button
                                      onClick={handleActivatePush}
                                      disabled={notifLoading}
                                      style={{
                                        marginTop: 4,
                                        padding: '6px 12px',
                                        borderRadius: 8,
                                        border: 'none',
                                        fontSize: '.72rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                        color: '#fff',
                                        opacity: notifLoading ? 0.7 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 4,
                                        boxShadow: '0 2px 6px rgba(245,158,11,0.2)',
                                      }}
                                    >
                                      {notifLoading ? <Loader2 size={12} style={{ animation: 'lk-spin 1s linear infinite' }} /> : null}
                                      {notifLoading ? 'Ativando...' : 'Ativar Notificações'}
                                    </button>
                                  )}
                                </div>
                              )}

                              {/* Alertas de Dispositivos Inativos (> 30 dias) */}
                              {inactiveDevices.map((d) => {
                                const timeInfo = formatRelativeDeviceAccess(d.ultimoAcesso, false);
                                return (
                                  <div
                                    key={d.id}
                                    style={{
                                      background: 'linear-gradient(135deg, rgba(244,63,94,0.08) 0%, rgba(225,29,72,0.08) 100%)',
                                      border: '1px solid rgba(244,63,94,0.3)',
                                      borderRadius: 12,
                                      padding: 12,
                                      marginBottom: 12,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 6,
                                      textAlign: 'left',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: '.76rem', color: '#e11d48' }}>
                                        <AlertTriangle size={13} />
                                        <span>Dispositivo Inativo (+30 dias)</span>
                                      </div>
                                      <span style={{ fontSize: '.60rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#ffe4e6', color: '#be123c' }}>
                                        {timeInfo.shortLabel}
                                      </span>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '.68rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.4 }}>
                                      O aparelho <strong>{d.nome || d.navegadorDetectado}</strong> ({d.sistemaOp || 'Sistema'}) não é acessado {timeInfo.label.toLowerCase()}.
                                    </p>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          revokeUserDevice(d.id);
                                          toast.success(`Dispositivo ${d.nome || d.navegadorDetectado} desconectado.`);
                                        }}
                                        style={{
                                          padding: '5px 10px',
                                          borderRadius: 6,
                                          border: 'none',
                                          fontSize: '.68rem',
                                          fontWeight: 700,
                                          cursor: 'pointer',
                                          background: '#e11d48',
                                          color: '#fff',
                                        }}
                                      >
                                        Desconectar
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setNotifState('idle');
                                          navigate(getPerfilRouteForUser(user?.perfil, location.pathname));
                                        }}
                                        style={{
                                          padding: '5px 10px',
                                          borderRadius: 6,
                                          border: '1px solid hsl(var(--border))',
                                          fontSize: '.68rem',
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          background: 'hsl(var(--card))',
                                          color: 'hsl(var(--foreground))',
                                        }}
                                      >
                                        Revisar no Perfil
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}

                              {totalSecurityAlerts === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px 8px', color: 'hsl(var(--muted-foreground))' }}>
                                  <CheckCircle2 size={24} style={{ color: '#10b981', margin: '0 auto 8px' }} />
                                  <p style={{ fontSize: '.78rem', margin: 0 }}>Tudo limpo! Nenhuma pendência de segurança.</p>
                                </div>
                              ) : unreadApprovals.length === 0 ? null : (
                                <div className="notif-list-container">
                                  {groupedApprovals.map((group) => {
                                    if (group.type === 'single' || group.items.length === 1) {
                                      const ap = group.items[0];
                                      const isExpired = ap.status === 'EXPIRADO' || new Date(ap.expiraEm).getTime() <= Date.now();
                                      return (
                                        <div key={ap.id} style={{
                                          padding: 10, borderRadius: 10, background: 'hsl(var(--muted))',
                                          border: '1px solid hsl(var(--border))', fontSize: '.75rem',
                                          display: 'flex', flexDirection: 'column', gap: 6,
                                          textAlign: 'left',
                                          opacity: isExpired ? 0.75 : 1,
                                        }}>
                                          <div style={{ fontWeight: 700, color: 'hsl(var(--foreground))', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <span>🔐</span>
                                            <span>{isExpired
                                              ? (ap.tipo === 'BIOMETRIA_DIARIA' ? 'Biometria Expirada' : 'Acesso Expirado (Histórico)')
                                              : (ap.tipo === 'BIOMETRIA_DIARIA' ? 'Confirmação Biométrica' : 'Nova solicitação de acesso')}</span>
                                          </div>
                                          <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '.7rem', lineHeight: 1.3 }}>
                                            {ap.contexto?.navegador ? `${cleanBrowserName(ap.contexto.navegador)} (${ap.contexto.sistemaOp || 'OS'})` : 'Dispositivo desconhecido'}
                                            {ap.contexto?.ip && <><br />IP: {ap.contexto.ip}</>}
                                          </div>
                                          {!isExpired && (
                                            <button
                                              onClick={() => {
                                                setNotifState('idle');
                                                markApprovalAsOpened(ap.id);
                                                navigate(`/security/approve/${ap.id}`);
                                              }}
                                              style={{
                                                marginTop: 4, width: '100%', padding: '6px', borderRadius: 6, border: 'none',
                                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                color: '#fff',
                                                fontWeight: 700, fontSize: '.72rem', cursor: 'pointer', textAlign: 'center',
                                              }}
                                            >
                                              Analisar Acesso
                                            </button>
                                          )}
                                        </div>
                                      );
                                    }

                                    // It's a group of multiple expired approvals
                                    const isExpanded = !!expandedGroups[group.id];
                                    const firstAp = group.items[0];

                                    return (
                                      <div key={group.id} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6,
                                        border: '1px solid hsl(var(--border))',
                                        borderRadius: 12,
                                        background: 'hsl(var(--muted))',
                                        padding: 6,
                                        opacity: 0.85
                                      }}>
                                        {/* Group Header card (representing the first item) */}
                                        <div
                                          onClick={() => toggleGroup(group.id)}
                                          style={{
                                            padding: 6,
                                            borderRadius: 8,
                                            background: 'transparent',
                                            fontSize: '.75rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 6,
                                            textAlign: 'left',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <div style={{ fontWeight: 700, color: 'hsl(var(--foreground))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                              <span>🔓</span>
                                              <span>{
                                                firstAp.tipo === 'BIOMETRIA_DIARIA' ? 'Biometrias Expiradas' :
                                                  firstAp.tipo === 'DEVICE_AUTH' ? 'Acessos de Dispositivo Expirados' :
                                                    firstAp.tipo === 'APROVACAO_SUPERIOR' ? 'Aprovações de Superior Expiradas' :
                                                      `Solicitações Expiradas (${firstAp.tipo})`
                                              }</span>
                                            </div>
                                            <span style={{
                                              background: 'hsl(var(--secondary))',
                                              color: 'hsl(var(--secondary-foreground))',
                                              fontSize: '0.62rem',
                                              padding: '2px 6px',
                                              borderRadius: 10,
                                              fontWeight: 800,
                                              border: '1px solid hsl(var(--border))',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 4
                                            }}>
                                              {group.items.length} {isExpanded ? '▲' : '▼'}
                                            </span>
                                          </div>

                                          {/* Render the details of the most recent one */}
                                          <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '.7rem', lineHeight: 1.3 }}>
                                            <strong>Último:</strong> {firstAp.contexto?.navegador ? `${cleanBrowserName(firstAp.contexto.navegador)} (${firstAp.contexto.sistemaOp || 'OS'})` : 'Dispositivo desconhecido'}
                                            {firstAp.contexto?.ip && <><br />IP: {firstAp.contexto.ip}</>}
                                          </div>
                                        </div>

                                        {/* Expanded sub-items */}
                                        {isExpanded && (
                                          <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 6,
                                            padding: '4px 6px 6px',
                                            borderTop: '1px solid hsl(var(--border))',
                                            marginTop: 2
                                          }}>
                                            {group.items.map((ap) => (
                                              <div key={ap.id} style={{
                                                padding: 8,
                                                borderRadius: 8,
                                                background: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                fontSize: '.72rem',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4,
                                                textAlign: 'left'
                                              }}>
                                                <div style={{ fontWeight: 600, color: 'hsl(var(--foreground))', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                  <span style={{ fontSize: '.65rem' }}>⏱️</span>
                                                  <span>{new Date(ap.expiraEm).toLocaleString('pt-BR')}</span>
                                                </div>
                                                <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '.68rem', lineHeight: 1.3 }}>
                                                  {ap.contexto?.navegador ? `${cleanBrowserName(ap.contexto.navegador)} (${ap.contexto.sistemaOp || 'OS'})` : 'Dispositivo desconhecido'}
                                                  {ap.contexto?.ip && <><br />IP: {ap.contexto.ip}</>}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          <div style={{ borderTop: '1px solid hsl(var(--border))', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {notifTab === 'comunicados' && unreadComunicados.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={markAllComunicadosAsRead}
                                  style={{
                                    background: 'none', border: 'none', fontSize: '.72rem', color: '#2563eb', fontWeight: 700, cursor: 'pointer', padding: 0
                                  }}
                                >
                                  Marcar lidos
                                </button>
                              ) : notifTab === 'seguranca' && unreadApprovals.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={clearAllNotifs}
                                  style={{
                                    background: 'none', border: 'none', fontSize: '.72rem', color: 'hsl(var(--muted-foreground))', fontWeight: 600, cursor: 'pointer', padding: 0
                                  }}
                                >
                                  Limpar histórico
                                </button>
                              ) : null}

                              <button
                                type="button"
                                onClick={() => {
                                  const currentModulePrefix =
                                    location.pathname.startsWith('/admin') ? '/admin' :
                                    location.pathname.startsWith('/municipal') ? '/municipal' :
                                    location.pathname.startsWith('/master') ? '/master' :
                                    location.pathname.startsWith('/equipe') ? '/equipe' :
                                    location.pathname.startsWith('/professor') ? '/professor' :
                                    location.pathname.startsWith('/estadual') ? '/estadual' :
                                    location.pathname.startsWith('/rede') ? '/rede' :
                                    location.pathname.startsWith('/aluno') ? '/aluno' :
                                    location.pathname.startsWith('/responsavel') ? '/responsavel' : '/admin';

                                  setNotifState('idle');
                                  navigate(`${currentModulePrefix}/alertas`);
                                }}
                                style={{
                                  background: 'none', border: 'none', fontSize: '.72rem', color: '#2563eb', fontWeight: 700, cursor: 'pointer', padding: 0,
                                  display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'underline'
                                }}
                              >
                                Ver Histórico
                              </button>
                            </div>

                            <button
                              type="button"
                              onClick={() => setNotifState('idle')}
                              style={{
                                padding: '4px 12px', borderRadius: 6, border: 'none',
                                background: 'hsl(var(--muted))', fontSize: '.75rem',
                                color: 'hsl(var(--muted-foreground))', cursor: 'pointer', fontWeight: 600,
                              }}
                            >
                              Fechar
                            </button>
                          </div>
                        </div>
                      </div>
                    </>,
                    document.body
                  )}
                </div>
              );
            })()}


            {/* User dropdown */}
            {!isMobileDevice && (
              <div ref={userMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 4px',
                    borderRadius: 8, height: 34, border: `1px solid ${envLabel ? 'rgba(255,255,255,0.3)' : 'hsl(var(--border))'}`,
                    background: envLabel ? 'rgba(255,255,255,0.95)' : (userMenuOpen ? 'hsl(var(--muted))' : 'transparent'),
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                  className={envLabel ? 'hover:opacity-90' : 'hover:bg-muted'}
                >
                  {/* Avatar */}
                  <UserAvatar
                    src={user?.fotoUrl}
                    name={user?.nome}
                    role={user?.cargo || user?.perfil}
                    size="xs"
                    style={{ border: 'none' }}
                  />
                  {/* Name (hidden on mobile) */}
                  <span className="hidden sm:inline" style={{ fontSize: '0.8rem', fontWeight: 600, color: envLabel ? '#1e1e1e' : 'hsl(var(--foreground))', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cleanedName.split(' ')[0] || 'Usuário'}
                  </span>
                  <ChevronDown size={14} style={{
                    color: envLabel ? '#666' : 'hsl(var(--muted-foreground))',
                    transform: userMenuOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                    flexShrink: 0,
                  }} />
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                    minWidth: 240, borderRadius: 12, overflow: 'hidden',
                    background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)', zIndex: 100,
                    animation: 'fadeIn 0.15s ease-out',
                  }}>
                    {/* User info header */}
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid hsl(var(--border))' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <UserAvatar
                          src={user?.fotoUrl}
                          name={user?.nome}
                          role={user?.cargo || user?.perfil}
                          size="md"
                        />
                        <div style={{ overflow: 'hidden' }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'hsl(var(--foreground))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {cleanedName || 'Usuário'}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                              padding: '1px 6px', borderRadius: 4, fontSize: '0.6rem', fontWeight: 700,
                              background: perfilColor + '18', color: perfilColor,
                            }}>{displayRole}</span>
                            {user?.email && <span style={{ opacity: 0.7 }}>{user.email.length > 22 ? user.email.substring(0, 22) + '…' : user.email}</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Menu items */}
                    {(() => {
                      const perfilRoute = getPerfilRouteForUser(user?.perfil, location.pathname);
                      const currentModulePrefix = perfilRoute.replace('/perfil', '');

                      const userMenuRoutes = {
                        perfilRoute,
                        alertasRoute: `${currentModulePrefix}/alertas`,
                        chatRoute: `${currentModulePrefix}/chat`,
                        settingsRoute: `${currentModulePrefix}/configuracoes`,
                      };

                      return (
                        <div style={{ padding: '6px' }}>
                          <button onClick={() => { setUserMenuOpen(false); navigate(userMenuRoutes.perfilRoute); }} style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
                            borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: '0.82rem', fontWeight: 500, color: 'hsl(var(--foreground))',
                            transition: 'background 0.15s',
                          }} className="hover:bg-muted">
                            <User size={16} style={{ opacity: 0.6 }} /> Meu Perfil
                          </button>
                          <button onClick={() => { setUserMenuOpen(false); navigate(userMenuRoutes.alertasRoute); }} style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
                            borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: '0.82rem', fontWeight: 500, color: 'hsl(var(--foreground))',
                            transition: 'background 0.15s',
                          }} className="hover:bg-muted">
                            <Megaphone size={16} style={{ opacity: 0.6 }} /> Meus Comunicados
                          </button>
                          <button onClick={() => { setUserMenuOpen(false); navigate(userMenuRoutes.settingsRoute); }} style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
                            borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: '0.82rem', fontWeight: 500, color: 'hsl(var(--foreground))',
                            transition: 'background 0.15s',
                          }} className="hover:bg-muted">
                            <Settings size={16} style={{ opacity: 0.6 }} /> Configurações
                          </button>

                          {isAdmin && (
                            <button
                              onClick={() => { setUserMenuOpen(false); handleOpenRestartModal(); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
                                borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer',
                                fontSize: '0.82rem', fontWeight: 500, color: '#dc2626',
                                transition: 'background 0.15s',
                              }}
                              className="hover:bg-muted"
                            >
                              <Database size={16} style={{ opacity: 0.8, color: '#dc2626' }} /> RESTART DB
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Logout */}
                    <div style={{ padding: '6px', borderTop: '1px solid hsl(var(--border))' }}>
                      <button onClick={handleLogout} style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
                        borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer',
                        fontSize: '0.82rem', fontWeight: 600, color: '#ef4444',
                        transition: 'background 0.15s',
                      }} className="hover:bg-muted">
                        <LogOut size={16} /> Sair
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
          {/* ═══ Neon Laser Line ═══ */}
          {envLabel && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, overflow: 'hidden' }}>
              {/* Base glow */}
              <div style={{
                position: 'absolute', inset: 0,
                background: isDev ? 'rgba(239,68,68,0.5)' : 'rgba(234,88,12,0.5)',
                boxShadow: isDev
                  ? '0 0 8px rgba(239,68,68,0.6), 0 0 20px rgba(239,68,68,0.3)'
                  : '0 0 8px rgba(234,88,12,0.6), 0 0 20px rgba(234,88,12,0.3)',
              }} />
              {/* Laser sweep */}
              <div className="ac-neon-laser" style={{
                position: 'absolute', top: 0, bottom: 0, width: '30%',
                background: isDev
                  ? 'linear-gradient(90deg, transparent, #ff6b6b, #fff, #ff6b6b, transparent)'
                  : 'linear-gradient(90deg, transparent, #fb923c, #fff, #fb923c, transparent)',
                boxShadow: isDev
                  ? '0 0 15px #ef4444, 0 0 30px #ef4444, 0 0 45px rgba(239,68,68,0.5)'
                  : '0 0 15px #ea580c, 0 0 30px #ea580c, 0 0 45px rgba(234,88,12,0.5)',
                borderRadius: 2,
              }} />
            </div>
          )}
        </header>
        <main className={`flex-1 flex flex-col ${
          (useShadcnSidebar && isMobileDevice)
            ? 'overflow-visible p-2'
            : location.pathname.startsWith('/responsavel') || location.pathname.startsWith('/professor')
              ? 'overflow-auto p-0 md:p-2 lg:p-4'
              : location.pathname === '/admin/dashboard' || location.pathname.endsWith('/setup')
                ? 'overflow-hidden p-0'
                : location.pathname.includes('/ecad-studio') || location.pathname.includes('/laboratorio-testes')
                  ? 'overflow-hidden p-0'
                  : 'overflow-auto p-2 lg:p-4'
          }`} style={{ position: 'relative' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: (useShadcnSidebar && isMobileDevice) ? 'auto' : 0 }}>
            {setupRouteRedirect && <Navigate to={setupRouteRedirect} replace />}
            {equipePathBlocked.block && equipePathBlocked.redirect && (
              <Navigate to={equipePathBlocked.redirect} replace />
            )}
            {equipePathBlocked.block && equipePathBlocked.noAccess && (
              <div
                className="admin-page"
                style={{ maxWidth: 520, margin: '0 auto', padding: 24, textAlign: 'center' }}
              >
                <h1 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>Sem permissão</h1>
                <p style={{ fontSize: '.9rem', color: 'var(--text-muted, #64748b)', lineHeight: 1.5 }}>
                  Sua conta não tem nenhuma permissão ativa neste painel. Peça ao diretor(a) para revisar suas permissões em Equipe.
                </p>
              </div>
            )}
            {!equipePathBlocked.block && !setupRouteRedirect && outletNode}
          </div>
        </main>
        {/* Mobile Bottom Navbar */}
        {!(location.pathname.startsWith('/professor') || location.pathname.startsWith('/responsavel')) && (
          <div className="ac-mobile-navbar md:hidden ac-mobile-navbar--dark">
            {mobileNavItems.map((item) => {
              const Icon = item.icon;
              const active = isItemActive(item.to);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => {
                    if (item.label === 'CHAT') {
                      setChatOpen(false);
                    }
                  }}
                  className={`ac-mobile-nav-item ${active ? 'ac-mobile-nav-item--active' : ''}`}
                >
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <Icon size={20} />
                    {item.label === 'CHAT' && chatUnreadCount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: -6,
                        right: -8,
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)',
                        boxShadow: '0 0 8px rgba(56, 189, 248, 0.4)',
                        color: 'white',
                        borderRadius: 10,
                        minWidth: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.55rem',
                        fontWeight: 800,
                        padding: '0 4px',
                        zIndex: 10
                      }}>
                        {chatUnreadCount}
                      </span>
                    )}
                  </div>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        )}

      </div>
          );
        };

        const exitingScreen = transitionQueue.find((s) => s.state === 'exiting');
        const enteringScreen = transitionQueue.find((s) => s.state === 'entering');
        const isTransitioning = Boolean(exitingScreen && enteringScreen);
        const slideDir = enteringScreen?.direction ?? 'forward';
        const slideDur = enteringScreen?.durationMs ?? 2550;
        const liveSlideClass = isTransitioning
          ? slideDir === 'forward'
            ? 'ac-pane-curr-forward'
            : 'ac-pane-curr-backward'
          : '';
        const exitSlideClass =
          slideDir === 'forward' ? 'ac-pane-prev-forward' : 'ac-pane-prev-backward';

        return (
          <div
            style={{
              position: 'relative',
              flex: 1,
              minWidth: 0,
              height: '100%',
              overflow: 'hidden',
              ['--ac-trans-dur' as any]: `${slideDur}ms`,
            }}
            className={
              isInitialLoad && !isMobileDevice && !isTransitioning
                ? 'ac-animate-content'
                : ''
            }
            data-ac-page-transition={isTransitioning ? '1' : undefined}
          >
            {/* Overlay da tela antiga — some no fim sem remountar o painel live */}
            {exitingScreen && (
              <div
                key={exitingScreen.id}
                className={`ac-transition-pane ac-transition-pane--exiting ${exitSlideClass}`}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  zIndex: 2,
                  pointerEvents: 'none',
                  background: 'var(--background-color, #f8fafc)',
                }}
              >
                {renderRightPane(exitingScreen.outlet, true)}
              </div>
            )}

            {/* Painel live estável: página nova monta no início do slide; no fim só remove o overlay */}
            <div
              key="ac-live-pane"
              className={`ac-live-pane ac-transition-pane ${liveSlideClass}`}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 1,
                background: 'var(--background-color, #f8fafc)',
              }}
            >
              {renderRightPane(currentOutlet || <Outlet />, false)}
            </div>
          </div>
        );
      })()}
      <TourPrepBanner />
      <ACWalkthrough />
      {help.dialogOpen && (
        <HelpDialog
          title={help.pageTitle || headerTitle || 'Ajuda'}
          topics={help.topics}
          selectedTopicId={help.selectedTopicId}
          onSelectTopic={help.selectTopic}
          onClose={help.closeHelp}
          hasWalkthrough={settings.showTour}
          onStartWalkthrough={() => {
            if (!toursOn) handleToggleTours(true);
            void handleStartTour();
          }}
          toursEnabled={toursOn && settings.showTour}
          onToggleTours={handleToggleTours}
          onRegenerateTour={() => {
            void handleRegenerateTour();
          }}
          regeneratingTour={regeneratingTour}
        />
      )}

      {showPairingModal && (
        <Modal
          open={showPairingModal}
          onClose={() => setShowPairingModal(false)}
          title="Conectar Dispositivo Móvel"
          size="default"
        >
          <div style={{ padding: '10px 0', minHeight: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <QrCodeVinculo
              onVinculado={() => {
                setShowPairingModal(false);
                import('sonner').then(m => m.toast.success('Dispositivo pareado com sucesso!'));
              }}
              onPular={() => setShowPairingModal(false)}
              somenteVinculo={true}
            />
          </div>
        </Modal>
      )}

      {showEmailVerifyModal && (
        <Modal
          open={showEmailVerifyModal}
          onClose={() => {
            if (!emailVerifyLoading && !emailVerifySuccess) {
              setShowEmailVerifyModal(false);
              setEmailVerifyError('');
            }
          }}
          title={!emailCodeSent ? 'Confirmação de E-mail' : 'Verificação de E-mail'}
          size="default"
          footer={
            !emailCodeSent ? (
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowEmailVerifyModal(false)}
                  disabled={emailVerifyLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-save"
                  onClick={handleTriggerEmailVerification}
                  disabled={emailVerifyLoading}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800 }}
                >
                  {emailVerifyLoading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                  Enviar Código de Verificação
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowEmailVerifyModal(false)}
                  disabled={emailVerifyLoading || emailVerifySuccess}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="email-verify-form"
                  className="btn-save"
                  disabled={emailVerifyLoading || emailVerifySuccess || emailVerifyCode.length < 6}
                >
                  {emailVerifyLoading ? <Loader2 size={16} className="animate-spin" /> : 'Confirmar'}
                </button>
              </div>
            )
          }
        >
          {!emailCodeSent ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                <Mail size={22} color="#4f46e5" className="flex-shrink-0" />
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Endereço para Confirmação
                  </p>
                  <p style={{ margin: 0, fontSize: '0.95rem', color: '#0f172a', fontWeight: 800, wordBreak: 'break-all' }}>
                    {user?.email || 'Nenhum e-mail registrado'}
                  </p>
                </div>
              </div>

              <p style={{ margin: 0, fontSize: '0.92rem', color: '#0f172a', lineHeight: 1.6, fontWeight: 500 }}>
                Para validar e proteger sua conta, enviaremos um código de segurança de 6 dígitos para o seu endereço de e-mail.
              </p>

              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', fontSize: '0.8rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={16} className="flex-shrink-0 text-emerald-600" />
                <span>Certifique-se de que tem acesso a esta caixa postal. O código será válido por 10 minutos.</span>
              </div>

              {emailVerifyError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontSize: '0.78rem', fontWeight: 600 }}>
                      <AlertTriangle size={14} className="flex-shrink-0" />
                      <span>{emailVerifyError}</span>
                    </div>
                    {emailVerifyErrorDetails && (
                      <button
                        type="button"
                        onClick={() => setShowErrorDetails(!showErrorDetails)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#991b1b',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          padding: '2px 4px',
                          borderRadius: 4,
                        }}
                      >
                        {showErrorDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        {showErrorDetails ? 'Ocultar' : 'Detalhes'}
                      </button>
                    )}
                  </div>

                  {showErrorDetails && emailVerifyErrorDetails && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #fca5a5' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.68rem', color: '#7f1d1d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Diagnóstico Técnico do Erro
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(emailVerifyErrorDetails);
                            setErrorCopied(true);
                            toast.success('Detalhes do erro copiados!');
                            setTimeout(() => setErrorCopied(false), 2000);
                          }}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #fca5a5',
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: '#991b1b',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          {errorCopied ? <CheckCircle2 size={10} color="#16a34a" /> : <Copy size={10} />}
                          {errorCopied ? 'Copiado!' : 'Copiar Detalhes'}
                        </button>
                      </div>
                      <pre
                        style={{
                          margin: 0,
                          padding: 8,
                          background: '#0f172a',
                          color: '#f8fafc',
                          borderRadius: 6,
                          fontSize: '0.68rem',
                          fontFamily: 'monospace',
                          maxHeight: 120,
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {emailVerifyErrorDetails}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <form id="email-verify-form" onSubmit={handleConfirmEmailVerification} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ margin: 0, fontSize: '0.92rem', color: '#0f172a', lineHeight: 1.6, fontWeight: 500 }}>
                Enviamos um código de verificação de 6 dígitos para o seu e-mail <strong style={{ color: '#000000', fontWeight: 800 }}>{user?.email}</strong>. Digite-o abaixo para confirmar sua conta:
              </p>

              {emailVerifySuccess ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#10b981', padding: '18px 0' }}>
                  <CheckCircle2 size={40} />
                  <span style={{ fontSize: '1rem', fontWeight: 800 }}>E-mail verificado com sucesso!</span>
                </div>
              ) : (
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label className="form-label" style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a', letterSpacing: '0.5px' }}>
                    Código de 6 dígitos
                  </label>
                  <input
                    ref={emailCodeInputRef}
                    type="text"
                    maxLength={6}
                    value={emailVerifyCode}
                    onChange={e => setEmailVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="0 0 0 0 0 0"
                    className="form-input"
                    style={{
                      textAlign: 'center',
                      fontSize: '1.65rem',
                      letterSpacing: '8px',
                      fontWeight: 800,
                      color: '#000000',
                      background: '#ffffff',
                      border: '2px solid #64748b',
                      borderRadius: 12,
                      padding: '12px 14px',
                    }}
                    disabled={emailVerifyLoading}
                    autoFocus
                    required
                  />
                  {emailVerifyError && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontSize: '0.78rem', fontWeight: 600 }}>
                          <AlertTriangle size={14} className="flex-shrink-0" />
                          <span>{emailVerifyError}</span>
                        </div>
                        {emailVerifyErrorDetails && (
                          <button
                            type="button"
                            onClick={() => setShowErrorDetails(!showErrorDetails)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#991b1b',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              padding: '2px 4px',
                              borderRadius: 4,
                            }}
                          >
                            {showErrorDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            {showErrorDetails ? 'Ocultar' : 'Detalhes'}
                          </button>
                        )}
                      </div>

                      {showErrorDetails && emailVerifyErrorDetails && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #fca5a5' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontSize: '0.68rem', color: '#7f1d1d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Diagnóstico Técnico do Erro
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(emailVerifyErrorDetails);
                                setErrorCopied(true);
                                toast.success('Detalhes do erro copiados!');
                                setTimeout(() => setErrorCopied(false), 2000);
                              }}
                              style={{
                                background: '#ffffff',
                                border: '1px solid #fca5a5',
                                borderRadius: 4,
                                padding: '2px 8px',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                color: '#991b1b',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              {errorCopied ? <CheckCircle2 size={10} color="#16a34a" /> : <Copy size={10} />}
                              {errorCopied ? 'Copiado!' : 'Copiar Detalhes'}
                            </button>
                          </div>
                          <pre
                            style={{
                              margin: 0,
                              padding: 8,
                              background: '#0f172a',
                              color: '#f8fafc',
                              borderRadius: 6,
                              fontSize: '0.68rem',
                              fontFamily: 'monospace',
                              maxHeight: 120,
                              overflowY: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                            }}
                          >
                            {emailVerifyErrorDetails}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!emailVerifySuccess && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={handleTriggerEmailVerification}
                    disabled={emailVerifyLoading || emailResendCooldown > 0}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: emailResendCooldown > 0 ? '#94a3b8' : 'hsl(var(--primary))',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: emailResendCooldown > 0 ? 'not-allowed' : 'pointer',
                      textDecoration: emailResendCooldown > 0 ? 'none' : 'underline',
                      opacity: emailVerifyLoading || emailResendCooldown > 0 ? 0.7 : 1,
                    }}
                  >
                    {emailResendCooldown > 0
                      ? `Aguarde ${emailResendCooldown}s para reenviar o código`
                      : 'Reenviar código de verificação'}
                  </button>

                  {(emailSendAttempts >= 2 || (emailVerifyError && emailVerifyError.toLowerCase().includes('inválido'))) && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', textAlign: 'center', width: '100%' }}>
                      <p style={{ margin: 0, fontSize: '0.72rem', color: '#475569' }}>
                        E-mail não responde ou digitado errado?{' '}
                        <button
                          type="button"
                          onClick={() => {
                            setShowEmailVerifyModal(false);
                            navigate(getPerfilRouteForUser(user?.perfil, location.pathname));
                          }}
                          style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: 800, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                        >
                          Alterar e-mail no Perfil
                        </button>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </form>
          )}
        </Modal>
      )}

      {/* Floating Screenshot Thumbnails (Right Side) */}
      {screenshots.length > 0 && (
        <div
          style={{
            position: 'fixed',
            right: 16,
            top: 80,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
            padding: 8,
            background: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(12px)',
            borderRadius: 16,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
            width: 100,
            alignItems: 'center',
            animation: 'slideInRight 0.2s ease-out'
          }}
        >
          <span style={{ fontSize: '0.52rem', fontWeight: 800, color: '#a78bfa', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Capturas ({screenshots.length})
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {screenshots.map((src, index) => (
              <div
                key={index}
                style={{
                  position: 'relative',
                  width: 76,
                  height: 48,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                  transition: 'transform 0.15s'
                }}
                className="hover-scale-105"
                onClick={() => setSelectedZoomImage(src)}
              >
                <img src={src} alt={`Print ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setScreenshots(prev => prev.filter((_, i) => i !== index));
                  }}
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    background: 'rgba(239, 68, 68, 0.85)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowFeedbackDialog(true)}
            className="btn-save"
            style={{
              width: '100%',
              padding: '8px 4px',
              fontSize: '0.68rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              borderRadius: 8,
              marginTop: 4
            }}
          >
            Concluir
          </button>
        </div>
      )}

      {/* Lightbox Screenshot Zoom */}
      {selectedZoomImage && (
        <Modal
          open={!!selectedZoomImage}
          onClose={() => setSelectedZoomImage(null)}
          title="Visualização da Captura"
          size="lg"
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <img
              src={selectedZoomImage}
              alt="Zoomed"
              style={{ width: '100%', height: 'auto', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}
            />
            <button
              className="btn-cancel"
              onClick={() => setSelectedZoomImage(null)}
            >
              Fechar
            </button>
          </div>
        </Modal>
      )}

      {/* Feedback Submission Dialog */}
      {showFeedbackDialog && (
        <Modal
          open={showFeedbackDialog}
          onClose={() => setShowFeedbackDialog(false)}
          title="Cadastrar Feedback de Erro / Sugestão"
          size="lg"
          footer={
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
              <button
                className="btn-cancel"
                onClick={() => setShowFeedbackDialog(false)}
                disabled={iaGenerating}
              >
                Cancelar
              </button>
              <button
                className="btn-save"
                disabled={iaGenerating || !feedbackTitle.trim()}
                onClick={() => {
                  import('sonner').then(m => m.toast.success('Feedback enviado com sucesso para a equipe interna!'));
                  setScreenshots([]);
                  setFeedbackTitle('');
                  setFeedbackDesc('');
                  setHasAudio(false);
                  setShowFeedbackDialog(false);
                }}
              >
                Enviar Feedback Interno
              </button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Carousel/Grid of screenshots */}
            <div>
              <label className="form-label" style={{ marginBottom: 6, display: 'block' }}>Imagens Capturadas ({screenshots.length})</label>
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
                {screenshots.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt="Feedback attachment"
                    style={{ height: 90, width: 160, objectFit: 'cover', borderRadius: 8, border: '2px solid rgba(255,255,255,0.1)' }}
                  />
                ))}
              </div>
            </div>

            {/* Audio recording mock */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px dashed rgba(255,255,255,0.15)',
              borderRadius: 12,
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: isMockRecordingAudio ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  color: isMockRecordingAudio ? '#ef4444' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: isMockRecordingAudio ? 'pulse 1.2s infinite' : 'none'
                }}>
                  <span>🎙️</span>
                </div>
                <div>
                  <h5 style={{ fontSize: '0.78rem', fontWeight: 700, margin: 0 }}>Áudio do Feedback</h5>
                  <p style={{ fontSize: '0.68rem', color: '#94a3b8', margin: 0 }}>
                    {isMockRecordingAudio
                      ? `Gravando... ${mockAudioDuration}s`
                      : (hasAudio ? 'Áudio gravado e anexado' : 'Sem áudio anexado')
                    }
                  </p>
                </div>
              </div>

              <button
                type="button"
                className={isMockRecordingAudio ? 'btn-cancel' : 'btn-save'}
                style={{ padding: '6px 12px', fontSize: '0.72rem' }}
                onClick={() => {
                  if (isMockRecordingAudio) {
                    setIsMockRecordingAudio(false);
                    setHasAudio(true);
                  } else {
                    setIsMockRecordingAudio(true);
                    setMockAudioDuration(0);
                    const interval = setInterval(() => {
                      setMockAudioDuration(d => {
                        if (d >= 30) {
                          clearInterval(interval);
                          setIsMockRecordingAudio(false);
                          setHasAudio(true);
                          return 30;
                        }
                        return d + 1;
                      });
                    }, 1000);
                  }
                }}
              >
                {isMockRecordingAudio ? 'Parar Gravador' : (hasAudio ? 'Gravar Novamente' : 'Gravar Relato')}
              </button>
            </div>

            {/* IA Wizard Fill */}
            <button
              type="button"
              className="btn-save"
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #1e40af 100%)',
                alignSelf: 'flex-start',
                fontSize: '0.76rem',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              disabled={iaGenerating}
              onClick={() => {
                setIaGenerating(true);
                import('sonner').then(m => {
                  m.toast.promise(
                    new Promise((resolve) => setTimeout(resolve, 2000)),
                    {
                      loading: 'IA Alice analisando as capturas de tela e áudio...',
                      success: () => {
                        setFeedbackTitle(`Erro de Fluxo na página ${currentPage?.label || 'Atual'}`);
                        setFeedbackDesc(`[Análise IA Alice]: O usuário identificou uma inconsistência visual ou de fluxo na tela de ${currentPage?.label}. As capturas anexadas mostram o estado atual da UI. Relato em áudio complementar processado.`);
                        setIaGenerating(false);
                        return 'Auto-preenchido pela IA com sucesso!';
                      },
                      error: 'Erro na geração de IA'
                    }
                  );
                });
              }}
            >
              ✨ IA Auto-Preencher Feedback
            </button>

            {/* Form Fields */}
            <div className="form-group">
              <label className="form-label">Título do Feedback *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Ex: Botão não responde ou erro de layout"
                value={feedbackTitle}
                onChange={e => setFeedbackTitle(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrição e Detalhes</label>
              <textarea
                className="form-input"
                rows={4}
                placeholder="Explique o que aconteceu ou o que pode ser melhorado..."
                value={feedbackDesc}
                onChange={e => setFeedbackDesc(e.target.value)}
              />
            </div>
          </div>
        </Modal>
      )}



      {/* Página de progresso do RESTART DB */}
      {restartProgress.open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
            background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 45%, #0f172a 100%)',
            color: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div style={{ width: 'min(520px, 100%)', textAlign: 'center' }}>
            <Database size={40} style={{ color: '#f87171', marginBottom: 16 }} />
            <h1 style={{ fontSize: '1.35rem', fontWeight: 800, marginBottom: 8 }}>
              Reiniciando banco de dados
            </h1>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 28, lineHeight: 1.5 }}>
              {restartProgress.message}
            </p>
            <div
              style={{
                height: 10,
                borderRadius: 999,
                background: 'rgba(148,163,184,0.25)',
                overflow: 'hidden',
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.max(4, restartProgress.progress)}%`,
                  background:
                    restartProgress.status === 'ERRO'
                      ? '#ef4444'
                      : restartProgress.status === 'CONCLUIDO'
                        ? '#22c55e'
                        : 'linear-gradient(90deg, #f97316, #ef4444)',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
            <div style={{ fontSize: '0.78rem', color: '#cbd5e1', marginBottom: 8 }}>
              {restartProgress.progress}% · etapa <strong>{restartProgress.step || '—'}</strong>
            </div>
            {restartProgress.status === 'EM_ANDAMENTO' && (
              <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 16 }}>
                Em produção o backup Cloud SQL roda antes do wipe. Não feche nem recarregue a página.
              </p>
            )}
            {restartProgress.status === 'CONCLUIDO' && (
              <p style={{ fontSize: '0.85rem', color: '#86efac', marginTop: 16 }}>
                Concluído. Redirecionando para o login…
              </p>
            )}
            {restartProgress.status === 'ERRO' && (
              <div style={{ marginTop: 20 }}>
                <p style={{ fontSize: '0.85rem', color: '#fca5a5', marginBottom: 16 }}>
                  {restartProgress.error || 'Falha no reinício.'}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setRestartProgress({
                      open: false,
                      progress: 0,
                      step: '',
                      message: '',
                      status: 'IDLE',
                    })
                  }
                  style={{
                    padding: '10px 18px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#334155',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para Reiniciar Banco de Dados (Restart DB) */}
      {showRestartModal && (
        <Modal
          open={showRestartModal}
          onClose={() => !restartLoading && setShowRestartModal(false)}
          title="Reiniciar Banco de Dados"
          size="default"
          footer={
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
              <button
                className="btn-cancel"
                disabled={restartLoading}
                onClick={() => setShowRestartModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn-save"
                style={{ background: '#dc2626', borderColor: '#b91c1c' }}
                disabled={restartLoading || !restartPassword.trim()}
                onClick={handleConfirmRestart}
              >
                {restartLoading ? 'Iniciando…' : 'Confirmar Reinício'}
              </button>
            </div>
          }
        >
          <div style={{ padding: '4px 0' }}>
            <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginBottom: 16 }}>
              Esta operação irá limpar todas as escolas, turmas, alunos, responsáveis, chamados, e logs do banco de dados, reconstruindo os dados de infraestrutura inicial do zero.
              {' '}
              <strong>Em produção um backup Cloud SQL é disparado automaticamente antes do wipe</strong> — você verá o progresso na tela seguinte.
            </p>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label" style={{ fontWeight: 'bold', marginBottom: 8, display: 'block' }}>
                Selecione os Administradores a Preservar:
              </label>
              <div style={{
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                maxHeight: 180,
                overflowY: 'auto',
                padding: '8px 12px',
                background: 'var(--bg-card)'
              }}>
                {restartAdmins.length === 0 ? (
                  <p style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Nenhum administrador encontrado.</p>
                ) : (
                  restartAdmins.map(admin => (
                    <label
                      key={admin.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        color: 'hsl(var(--foreground))'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={admin.checked}
                        onChange={() => handleToggleAdminCheck(admin.id)}
                        style={{ cursor: 'pointer' }}
                        disabled={restartLoading}
                      />
                      <span>{admin.nome} <span style={{ opacity: 0.6 }}>({admin.email})</span></span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label" style={{ fontWeight: 'bold' }}>
                Senha de Confirmação:
              </label>
              <input
                type="password"
                className="form-input"
                placeholder="Digite a senha padrão"
                value={restartPassword}
                onChange={(e) => setRestartPassword(e.target.value)}
                disabled={restartLoading}
              />
            </div>

            {restartDateError && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fee2e2',
                color: '#b91c1c',
                fontSize: '0.78rem',
                padding: '8px 12px',
                borderRadius: 6,
                marginTop: 12
              }}>
                {restartDateError}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Global Internal Chat Widget (renderizado como floating/drawer nas demais páginas; a rota /chat utiliza o modo embedded) */}
      {!location.pathname.endsWith('/chat') && (
        <GlobalChatWidget
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          isDocked={isDocked}
          onToggleDock={toggleDock}
          chatUnreadCount={chatUnreadCount}
          setChatUnreadCount={setChatUnreadCount}
        />
      )}

      {/* ── Theme Customizer Modal ── */}
      {showThemeModal && (
        <Modal
          open={showThemeModal}
          onClose={() => setShowThemeModal(false)}
          title="Personalizar Cores e Layout"
          size="default"
        >
          <div className="space-y-4 font-sans text-slate-800 p-2">

            {/* Tabs */}
            <div className="flex border-b border-slate-100 pb-2">
              <button
                onClick={() => setActiveThemeTab('presets')}
                className={`flex-1 py-1.5 text-xs font-bold text-center border-b-2 transition-colors ${activeThemeTab === 'presets'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
              >
                Modelos de Tema
              </button>
              <button
                onClick={() => setActiveThemeTab('advanced')}
                className={`flex-1 py-1.5 text-xs font-bold text-center border-b-2 transition-colors ${activeThemeTab === 'advanced'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
              >
                Ajustes Finos (Herdado)
              </button>
            </div>

            {activeThemeTab === 'presets' ? (
              <div className="space-y-4">
                <p className="text-[11px] text-slate-400">Escolha um dos modelos visuais prontos para aplicar instantaneamente:</p>
                <div className="grid grid-cols-2 gap-3">
                  {THEME_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => {
                        setThemeSettings(preset);
                        localStorage.setItem('ac_custom_theme', JSON.stringify(preset));
                        import('sonner').then(m => m.toast.success(`Tema "${preset.name}" aplicado!`));
                      }}
                      className={`flex flex-col items-start p-3 rounded-2xl border text-left transition-all ${themeSettings.name === preset.name
                        ? 'border-indigo-600 ring-2 ring-indigo-500/20 bg-indigo-50/20'
                        : 'border-slate-100 hover:border-slate-200 hover:bg-slate-55/50'
                        }`}
                    >
                      <span className="text-xs font-bold text-slate-800">{preset.name}</span>
                      <div className="flex gap-1.5 mt-2.5">
                        <span 
                          className="w-5 h-5 rounded-full border border-white shadow-sm" 
                          style={{ background: `linear-gradient(135deg, ${preset.primaryStart}, ${preset.primaryEnd})` }} 
                          title="Destaque" 
                        />
                        <span className="w-5 h-5 rounded-full border border-white shadow-sm" style={{ background: preset.sidebar }} title="Menu" />
                        <span className="w-5 h-5 rounded-full border border-white shadow-sm" style={{ background: preset.background }} title="Fundo" />
                        <span className="w-5 h-5 rounded-full border border-white shadow-sm" style={{ background: preset.card }} title="Cards" />
                      </div>
                    </button>
                  ))}
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-4">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ajuste de Zoom e Tamanho das Fontes</h4>
                  
                  {/* Slider 1: Sidebar */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-slate-700">Menu Lateral (Sidebar)</label>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded">{themeSettings.fontSizeSidebar || themeSettings.fontSize || '14'}px</span>
                    </div>
                    <input
                      type="range"
                      min="12"
                      max="30"
                      value={themeSettings.fontSizeSidebar || themeSettings.fontSize || '14'}
                      onChange={(e) => {
                        const next = { ...themeSettings, fontSizeSidebar: e.target.value };
                        setThemeSettings(next);
                        localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                      }}
                      className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    />
                    <div className="flex justify-between text-[9px] text-slate-400">
                      <span>Pequeno (12px)</span>
                      <span>Padrão (14px)</span>
                      <span>Gigante (30px)</span>
                    </div>
                  </div>

                  {/* Slider 2: Header */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-slate-700">Cabeçalho (Header)</label>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded">{themeSettings.fontSizeHeader || themeSettings.fontSize || '14'}px</span>
                    </div>
                    <input
                      type="range"
                      min="12"
                      max="30"
                      value={themeSettings.fontSizeHeader || themeSettings.fontSize || '14'}
                      onChange={(e) => {
                        const next = { ...themeSettings, fontSizeHeader: e.target.value };
                        setThemeSettings(next);
                        localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                      }}
                      className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    />
                    <div className="flex justify-between text-[9px] text-slate-400">
                      <span>Pequeno (12px)</span>
                      <span>Padrão (14px)</span>
                      <span>Gigante (30px)</span>
                    </div>
                  </div>

                  {/* Slider 3: Body */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-slate-700">Conteúdo Principal (Body)</label>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded">{themeSettings.fontSizeBody || themeSettings.fontSize || '14'}px</span>
                    </div>
                    <input
                      type="range"
                      min="12"
                      max="30"
                      value={themeSettings.fontSizeBody || themeSettings.fontSize || '14'}
                      onChange={(e) => {
                        const next = { ...themeSettings, fontSizeBody: e.target.value };
                        setThemeSettings(next);
                        localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                      }}
                      className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    />
                    <div className="flex justify-between text-[9px] text-slate-400">
                      <span>Pequeno (12px)</span>
                      <span>Padrão (14px)</span>
                      <span>Gigante (30px)</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-400">Ajuste as cores de forma herdada. As páginas herdarão estas paletas:</p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Destaques Início (Gradiente Start)</label>
                    <div className="flex items-center gap-2 border border-slate-100 rounded-xl p-1.5 bg-slate-55/50">
                      <input
                        type="color"
                        value={themeSettings.primaryStart || themeSettings.primary}
                        onChange={(e) => {
                          const next = { ...themeSettings, name: 'Personalizado', primaryStart: e.target.value };
                          setThemeSettings(next);
                          localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                        }}
                        className="w-7 h-7 rounded-lg border-0 cursor-pointer p-0 bg-transparent flex-shrink-0"
                      />
                      <span className="text-[10px] font-mono font-semibold uppercase">{themeSettings.primaryStart || themeSettings.primary}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Destaques Fim (Gradiente End)</label>
                    <div className="flex items-center gap-2 border border-slate-100 rounded-xl p-1.5 bg-slate-55/50">
                      <input
                        type="color"
                        value={themeSettings.primaryEnd || themeSettings.primary}
                        onChange={(e) => {
                          const next = { ...themeSettings, name: 'Personalizado', primaryEnd: e.target.value };
                          setThemeSettings(next);
                          localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                        }}
                        className="w-7 h-7 rounded-lg border-0 cursor-pointer p-0 bg-transparent flex-shrink-0"
                      />
                      <span className="text-[10px] font-mono font-semibold uppercase">{themeSettings.primaryEnd || themeSettings.primary}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Menu (Sidebar)</label>
                    <div className="flex items-center gap-2 border border-slate-100 rounded-xl p-1.5 bg-slate-55/50">
                      <input
                        type="color"
                        value={themeSettings.sidebar}
                        onChange={(e) => {
                          const next = { ...themeSettings, name: 'Personalizado', sidebar: e.target.value };
                          setThemeSettings(next);
                          localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                        }}
                        className="w-7 h-7 rounded-lg border-0 cursor-pointer p-0 bg-transparent flex-shrink-0"
                      />
                      <span className="text-[10px] font-mono font-semibold uppercase">{themeSettings.sidebar}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fundo das Páginas</label>
                    <div className="flex items-center gap-2 border border-slate-100 rounded-xl p-1.5 bg-slate-55/50">
                      <input
                        type="color"
                        value={themeSettings.background}
                        onChange={(e) => {
                          const next = { ...themeSettings, name: 'Personalizado', background: e.target.value };
                          setThemeSettings(next);
                          localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                        }}
                        className="w-7 h-7 rounded-lg border-0 cursor-pointer p-0 bg-transparent flex-shrink-0"
                      />
                      <span className="text-[10px] font-mono font-semibold uppercase">{themeSettings.background}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fundo dos Cards</label>
                    <div className="flex items-center gap-2 border border-slate-100 rounded-xl p-1.5 bg-slate-55/50">
                      <input
                        type="color"
                        value={themeSettings.card}
                        onChange={(e) => {
                          const next = { ...themeSettings, name: 'Personalizado', card: e.target.value };
                          setThemeSettings(next);
                          localStorage.setItem('ac_custom_theme', JSON.stringify(next));
                        }}
                        className="w-7 h-7 rounded-lg border-0 cursor-pointer p-0 bg-transparent flex-shrink-0"
                      />
                      <span className="text-[10px] font-mono font-semibold uppercase">{themeSettings.card}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-4 border-t border-slate-100 mt-4">
                  <button
                    onClick={() => {
                      const defaultPreset = THEME_PRESETS[0];
                      setThemeSettings(defaultPreset);
                      localStorage.setItem('ac_custom_theme', JSON.stringify(defaultPreset));
                      import('sonner').then(m => m.toast.success('Restaurado para o padrão Aurora Boreal!'));
                    }}
                    className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-55 transition-colors"
                  >
                    Restaurar Padrão
                  </button>
                  <button
                    onClick={() => setShowThemeModal(false)}
                    className="px-4 py-1.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all"
                  >
                    Pronto
                  </button>
                </div>
              </div>
            )}

          </div>
        </Modal>
      )}

      {/* Modal de Autorização de Pasta Restrita */}
      <PasswordConfirmModal
        open={!!unlockFolderTarget}
        title={`Acesso Restrito: ${unlockFolderTarget || 'Itens Bloqueados'}`}
        description={`Para acessar os itens de ${unlockFolderTarget?.toLowerCase() || 'outros'}, digite a palavra-chave de autorização:`}
        requiredPassword="marcelo"
        confirmLabel="Liberar Acesso"
        onCancel={() => setUnlockFolderTarget(null)}
        onConfirm={() => {
          setIsDesativadosUnlocked(true);
          if (unlockFolderTarget) {
            setOpenFolders(prev => ({ ...prev, [unlockFolderTarget]: true }));
          }
          setUnlockFolderTarget(null);
        }}
      />

      {/* Modal de Visualização Oficial do Comunicado (Layout de Envio) */}
      <ComunicadoVisualizacaoModal
        open={!!visualizandoComunicado}
        onClose={() => setVisualizandoComunicado(null)}
        comunicadoInicial={visualizandoComunicado}
        listaComunicados={recentComunicados}
        readIds={readComunicadoIds}
        onMarkAsRead={markComunicadoAsRead}
        userPerfil={user?.perfil}
        onViewAllRoute={
          user?.perfil === 'MUNICIPAL'
            ? '/municipal/alertas'
            : user?.perfil === 'PROFESSOR'
              ? '/professor/alertas'
              : user?.perfil === 'RESPONSAVEL'
                ? '/responsavel/comunicados'
                : user?.perfil === 'ALUNO'
                  ? '/aluno/alertas'
                  : user?.perfil === 'ADMIN'
                    ? '/master/alertas'
                    : user?.perfil === 'ESTADUAL'
                      ? '/estadual/alertas'
                      : user?.perfil === 'REDE'
                        ? '/rede/alertas'
                        : '/admin/alertas'
        }
      />

      {/* Banner de Auditoria e Telemetria de Versão no Rodapé (Desktop & Mobile) */}
      <AuditFooterBanner />
    </div>
  );

  if (useShadcnSidebar) {
    return (
      <SidebarProvider defaultOpen={sidebarExpanded} onOpenChange={setSidebarExpanded}>
        {layoutContent}
      </SidebarProvider>
    );
  }

  return layoutContent;
}
//#endregion AC_BLOCK base-layout.base-layout

// Injected styles for notification animations
const notifStyles = document.createElement('style');
notifStyles.textContent = `
  :root {
    --ac-trans-dur: 2.55s;
    --ac-trans-bezier: cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes ac-sidebar-slide-in {
    from {
      opacity: 0;
      transform: translateX(-100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  .ac-animate-sidebar {
    animation: ac-sidebar-slide-in 2.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  .ac-animate-content {
    animation: ac-first-load-slide-in var(--ac-trans-dur) var(--ac-trans-bezier) 0.36s forwards;
    opacity: 0;
  }

  /* Animacao de transição direcional de telas (iOS/macOS Deck push/pop) */
  .ac-pane-prev-forward {
    animation: ac-content-slide-out-left var(--ac-trans-dur) var(--ac-trans-bezier) forwards;
  }
  .ac-pane-curr-forward {
    animation: ac-content-slide-in-left var(--ac-trans-dur) var(--ac-trans-bezier) forwards;
  }
  .ac-pane-prev-backward {
    animation: ac-content-slide-out-right var(--ac-trans-dur) var(--ac-trans-bezier) forwards;
  }
  .ac-pane-curr-backward {
    animation: ac-content-slide-in-right var(--ac-trans-dur) var(--ac-trans-bezier) forwards;
  }

  @keyframes ac-first-load-slide-in {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes ac-content-slide-in-left {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes ac-content-slide-out-left {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }

  @keyframes ac-content-slide-in-right {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes ac-content-slide-out-right {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(100%);
    }
  }
  .ac-transition-pane {
    will-change: transform;
  }
  .ac-transition-pane--exiting {
    pointer-events: none !important;
  }
  /* Evita fadeIn da página competindo com o slide (causa piscada no fim) */
  [data-ac-page-transition="1"] .animate-fadeIn,
  [data-ac-page-transition="1"] .animate-fade-in,
  [data-ac-page-transition="1"] .animate-fade-in-up,
  [data-ac-page-transition="1"] .animate-slideUp {
    animation: none !important;
    opacity: 1 !important;
  }

  /* Mobile: sem deslizamento de páginas (só desktop mantém) */
  @media (max-width: 768px) {
    .ac-animate-content,
    .ac-pane-prev-forward,
    .ac-pane-curr-forward,
    .ac-pane-prev-backward,
    .ac-pane-curr-backward,
    .ac-pane-prev-forward main,
    .ac-pane-curr-forward main,
    .ac-pane-prev-backward main,
    .ac-pane-curr-backward main,
    .ac-pane-curr-forward header,
    .ac-pane-curr-backward header,
    .ac-pane-curr-forward .ac-mobile-navbar,
    .ac-pane-curr-backward .ac-mobile-navbar {
      animation: none !important;
      opacity: 1 !important;
      transform: none !important;
    }
  }

  html.ac-cockpit-embed .ac-animate-content,
  html.ac-cockpit-embed .ac-pane-prev-forward,
  html.ac-cockpit-embed .ac-pane-curr-forward,
  html.ac-cockpit-embed .ac-pane-prev-backward,
  html.ac-cockpit-embed .ac-pane-curr-backward,
  html.ac-cockpit-embed .ac-transition-pane {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }

  @keyframes ac-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  aside *::-webkit-scrollbar,
  .ac-sidebar-scroll::-webkit-scrollbar,
  .ac-layout-sidebar::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  aside,
  .ac-sidebar-scroll,
  .ac-layout-sidebar {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
  @keyframes notif-dot-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.3); }
  }
  @keyframes notif-bell-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.06); }
  }
  @keyframes lk-spin { to { transform: rotate(360deg); } }
  @keyframes ac-neon-sweep {
    0% { left: -30%; opacity: 0.3; }
    15% { opacity: 1; }
    50% { left: 100%; opacity: 0.3; }
    51% { left: 100%; opacity: 0; }
    52% { left: -30%; opacity: 0; }
    53% { left: -30%; opacity: 0.3; }
    65% { opacity: 1; }
    100% { left: 100%; opacity: 0.3; }
  }
  .ac-neon-laser {
    animation: ac-neon-sweep 3s ease-in-out infinite;
  }
  .ac-env-header .ac-header-help-btn {
    background: rgba(255,255,255,0.95) !important;
    color: #1e1e1e !important;
    border-color: rgba(255,255,255,0.3) !important;
  }
  .ac-env-header .ac-header-help-btn:hover {
    opacity: 0.8;
  }
  @keyframes ac-bell-pulse-red {
    0%, 100% {
      border-color: #ef4444;
      box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.4);
    }
    50% {
      border-color: #f87171;
      box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.2);
    }
  }
  .ac-bell-pulsing-red {
    animation: ac-bell-pulse-red 1.6s infinite ease-in-out !important;
  }
  @keyframes ac-bell-pulse-yellow {
    0%, 100% {
      border-color: #fbbf24;
      box-shadow: 0 0 0 0px rgba(251, 191, 36, 0.4);
    }
    50% {
      border-color: #fcd34d;
      box-shadow: 0 0 0 6px rgba(251, 191, 36, 0.2);
    }
  }
  .ac-bell-pulsing-yellow {
    animation: ac-bell-pulse-yellow 1.8s infinite ease-in-out !important;
  }
  .ac-mobile-navbar {
    display: none;
  }
  @media (max-width: 767px) {
    .ac-mobile-navbar {
      display: flex;
      align-items: center;
      justify-content: space-around;
      height: 56px;
      background: linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%);
      border-top: 1px solid rgba(255, 255, 255, 0.15);
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.25);
      flex-shrink: 0;
      padding: 0 8px;
    }
    .ac-mobile-navbar--dark {
      background: #000000 !important;
      backdrop-filter: blur(20px) saturate(180%) !important;
      -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
      border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
      height: calc(66px + env(safe-area-inset-bottom, 0px)) !important;
      padding-bottom: env(safe-area-inset-bottom, 0px) !important;
      box-shadow: none !important;
    }
    .ac-mobile-nav-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.6rem;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s;
      flex: 1;
      height: 100%;
      gap: 2px;
    }
    .ac-mobile-navbar--dark .ac-mobile-nav-item {
      color: #8e8e93 !important;
      font-size: 0.56rem !important;
      font-weight: 600 !important;
      letter-spacing: 0.2px !important;
      gap: 3px !important;
    }
    .ac-mobile-nav-item--active {
      color: #ffffff;
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.4);
    }
    .ac-mobile-navbar--dark .ac-mobile-nav-item--active {
      color: #2563eb !important;
      text-shadow: none !important;
      font-weight: 700 !important;
    }
    .ac-mobile-nav-item--active svg {
      transform: translateY(-1px);
      color: #ffffff;
      filter: drop-shadow(0 2px 5px rgba(255, 255, 255, 0.3));
    }
    .ac-mobile-navbar--dark .ac-mobile-nav-item--active svg {
      color: #2563eb !important;
      filter: drop-shadow(0 2px 5px rgba(37, 99, 235, 0.3)) !important;
      transform: translateY(-1px) !important;
    }
    .ac-mobile-nav-item svg {
      transition: transform 0.2s, color 0.2s;
    }
    
    .ac-mobile-dark-header {
      background: #000000 !important;
      backdrop-filter: blur(20px) saturate(180%) !important;
      -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
      color: #ffffff !important;
    }
    .ac-mobile-dark-header .font-semibold {
      color: #ffffff !important;
    }
    .ac-mobile-dark-header button {
      color: rgba(255, 255, 255, 0.85) !important;
      border-color: rgba(255, 255, 255, 0.15) !important;
      background: transparent !important;
    }
    .ac-mobile-dark-header button.md\:hidden {
      color: #ffffff !important;
      background: transparent !important;
      border: none !important;
    }
    .ac-mobile-dark-header button:hover {
      background: rgba(255, 255, 255, 0.08) !important;
      color: #ffffff !important;
    }
  }
  @keyframes slideInRight {
    from { transform: translateX(120%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  .hover-scale-105:hover {
    transform: scale(1.05);
  }
`;
if (!document.querySelector('#ac-notif-styles')) {
  notifStyles.id = 'ac-notif-styles';
  document.head.appendChild(notifStyles);
}
